import moment from "moment/moment";
import appConfig from "../appConfig";
import { ModeratorPermissions, RoleIds } from "../containers/teamsContainer/domain";
import { toast } from "react-toastify";
import { CourseService } from "../api/services/course";
import { setCookieValue } from "./cookieStorage";
import momentTimezone from "moment-timezone";
import getSymbolFromCurrency from "currency-symbol-map";
import eventsUtils from "../containers/events/eventsUtils";
import { SSOMethod } from "../containers/IntegrationsContainer/SingleSignOn/domain";
import { ISizeValue, TSize } from "types";
import { SUBSCRIPTION_PLAN_INTERVALS, USER_SUBSCRIPTION_STATUS } from "../enums";
import { BYTES_IN_MB } from "../constants/common";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import isBetween from 'dayjs/plugin/isBetween';
import relativeTime from 'dayjs/plugin/relativeTime';
import en from 'dayjs/locale/en';
import { BunnyLibraryTypes } from "./domain";
import { ContentState, convertFromHTML } from 'draft-js';
import { BillingCycles, PlanNames, titleCasePlanName } from "../containers/groupAppPricing/domain";
import { channelAccessRestricted } from "../containers/groupView/domain";
import { loadStripe } from "@stripe/stripe-js";
import Gleap from "gleap";
import { LibraryAccess, isCohortCourse } from "../containers/Library/Admin/domain";
import { isWhiteColor } from "./themeTokens";
import { determinePurchaseNavigation, navigateToCoursePurchase } from "../containers/Library/Community/Courses/Lectures/utils";

let heic2any: any;
if (typeof window !== "undefined") {
  import("heic2any")
    .then((module) => {
      heic2any = module.default;
    })
    .catch((error) => {
      console.error("Error loading heic2any:", error);
    });
}

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(isBetween);
dayjs.extend(relativeTime);

dayjs.locale({
  ...en,
  name: 'en-short',
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: '%d sec',
    m: '1 min',
    mm: '%d min',
    h: '1 hr',
    hh: '%d hr',
    d: '1 day',
    dd: '%d days',
    M: '1 mo',
    MM: '%d mo',
    y: '1 yr',
    yy: '%d yr',
  },
} as any, undefined, true);

const logger = (title: string, ...data: any[]) => {
  const randomColor = getRandomColor();

  window.console.log(
    `\n\n%c =======  ${title} ======= %c${moment().format("HH:mm:ss SSS")} \n`,
    `color:${randomColor};font-size:15`,
    `color:${getRandomColor()};font-size:15`
  );
  window.console.log(``);
  window.console.log(`    `, data);
  window.console.log(`\n =========================================`);
};

const getRandomColor = () => {
  const letters = "0123456789ABCDEF";
  let color = "#";
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

const updateObject = (oldObject: any, updatedProperties: any) => {
  return {
    ...oldObject,
    ...updatedProperties
  };
};

const getStateSlice = (state: any) => state.toJS().locale;

const getNameInitials = (name: any = "") => {
  const nameStr = typeof name === 'string' ? name : String(name || '');
  let abbr = "";
  let str: any = nameStr || "";
  str = str.split(" ");
  for (let i = 0; i < str.length; i++) {
    abbr += str[i].substr(0, 1);
  }
  if (abbr) {
    if (abbr[1]) {
      return abbr[0].toUpperCase() + abbr[1].toUpperCase();
    }
    return abbr[0].toUpperCase();
  }
};

const getColorInitial = (name: any = "") => {
  const nameStr = typeof name === 'string' ? name : String(name || '');
  if (nameStr && nameStr.length > 1) {
    let abbr = "";
    let str: any = nameStr || "";
    str = str.split(" ");
    for (let i = 0; i < str.length; i++) {
      abbr += str[i].substr(0, 1);
    }

    return abbr[0].toUpperCase();
  }
};

const convertGroupLink = (link: any = "") => {
  let groupLink = link.replace(/^(?:https?:\/\/)?(?:www\.)?/i, "") || "";
  groupLink = groupLink.split("/")[0];

  return groupLink;
};

const supportVideoFormats = (file: any) => {
  const extension = file.name.slice(-3);
  return file.type !== "video/mp4"
    && file.type !== "video/mov"
    && file.type !== "video/avi"
    && file.type !== "video/x-flv"
    && file.type !== "video/quicktime"
    && file.type !== "video/webm"
    && extension !== "flv";
};

const isVideoFile = (file: any) => {
  if (!file) {
    return false;
  }

  if (file.type && file.type.toLowerCase().indexOf('video') === 0) {
    return true;
  }

  const fileName = (file.name || '').toLowerCase();
  return ['.mp4', '.mov', '.avi', '.flv', '.webm', '.mkv', '.m4v', '.wmv', '.mpeg', '.mpg', '.3gp', '.ogv']
    .some((extension: string) => fileName.endsWith(extension));
};

const isAdmin = (userMembership: any) => {
  return userMembership !== null && userMembership !== undefined && (userMembership.role && userMembership.role.id === RoleIds.Admin || userMembership.role && userMembership.role.id === RoleIds.Owner);
};

const isOwner = (userMembership: any) => {
  return userMembership !== null && userMembership !== undefined && userMembership.role && userMembership.role.name === "owner";
};

const isModerator = (userMembership: any) => {
  return userMembership !== null && userMembership !== undefined && userMembership.role && userMembership.role.id === RoleIds.Moderator;
};

const isTeamMember = (userMembership: any) => {
  return isAdmin(userMembership) || isModerator(userMembership);
};

// dms_disabled is a member's own "prevent members from messaging me" switch. For a regular
// member it cuts both ways — they can't be messaged and can't start messages — but admins
// and moderators keep their reach: they can always message, and always be messaged.
const canStartDirectMessages = (viewerMembership: any) => {
  return !(viewerMembership && viewerMembership.dms_disabled) || isTeamMember(viewerMembership);
};

const canDirectMessage = (viewerMembership: any, targetMembership: any) => {
  // can_message is computed by the backend for the authenticated viewer (both sides'
  // dms_disabled, blocks either way, team exemptions). Fall back to the client-side
  // dms_disabled computation for payloads that don't carry it yet.
  if (targetMembership && typeof targetMembership.can_message === 'boolean') {
    return targetMembership.can_message;
  }
  const targetAccepts = !(targetMembership && targetMembership.dms_disabled) || isTeamMember(viewerMembership);
  const viewerAccepts = canStartDirectMessages(viewerMembership) || isTeamMember(targetMembership);
  return targetAccepts && viewerAccepts;
};

// null when messaging is allowed, otherwise the sentence to show the viewer.
const directMessageBlockedReason = (viewerMembership: any, targetMembership: any) => {
  if (canDirectMessage(viewerMembership, targetMembership)) {
    return null;
  }
  const name = (targetMembership && targetMembership.user && targetMembership.user.full_name) || 'this member';
  return `You can't message ${name}.`;
};

// True only when the group has a transcription add-on subscription with an active
// Stripe status. Gates the "Transcribe" option everywhere it's offered.
const hasActiveTranscriptionAddOn = (group: any) => {
  return Boolean(
    group &&
    group.add_on_subscriptions &&
    group.add_on_subscriptions.some(
      (sub: any) =>
        sub &&
        sub.stripe_status === "active" &&
        (sub.type || "").toLowerCase().indexOf("transcription") !== -1
    )
  );
};

const isGroupMember = (group: any) => {
  return group && isActiveCommunityMember(group.user_membership);
};

function getCourseButtonActionText(group: any, content: any, userMembership: any) {

  const showExpiredCTA = () => {
    // switch(content.access) {
    //   case "free":
    //     return false;
    //   case "paid":
    //     return (
    //       userMembership &&
    //       userMembership.active_subscription &&
    //       userMembership.active_subscription.plan &&
    //       !userMembership.active_subscription.plan.price
    //     )
    //   case "priced":
    //     return true;
    //   case "plan":
    //       return !userIsOnResourcePlan(userMembership, content.authorized_plans);
    //   case "segment":
    //     return !userIsOnResourseSegment(userMembership, content.authorized_segments);
    //   default:
    //       return false;
    // }

    return (
      content &&
      content.active_enrolment &&
      content.active_enrolment.expired_at &&
      !(
        isAdmin(userMembership) ||
        (
          isModerator(userMembership) &&
          moderatorIsAuthorizedForCourse(
            userMembership,
            content,
          )
        )
      )
    )
  }

  if (
    // content &&
    // content.active_enrolment &&
    // content.active_enrolment.expired_at &&
    // !isTeamMember(userMembership) &&
    // !showExpiredCTA()
    showExpiredCTA()
  ) {
    return "Expired";
  }


  if (!((content.active_enrolment) && !(content.active_enrolment && content.active_enrolment.expired_at)) && content.access === "plan") {

    if (!userMembership) {
      // Not logged in: show "Enroll in Course" (not "Upgrade") for plan-gated
      // courses. getCourseButtonAction routes "Enroll in Course" + plan access to
      // /plan-subscription (see the actionText === "Enroll in Course" branch), so
      // only the label changes, not the behaviour.
      return "Enroll in Course";
    }

    if (
      isAdmin(userMembership) ||
      (
        isModerator(userMembership) &&
        moderatorIsAuthorizedForCourse(
          userMembership,
          content,
        )
      )
    ) {
      return "Start";
    }

    if (userMembership && userMembership.active_subscription) {
      const plan = content.authorized_plans.find((plan: any) => {
        return plan.id === userMembership.active_subscription.plan.id;
      });
      return (plan || (
        isAdmin(userMembership) ||
        (
          isModerator(userMembership) &&
          moderatorIsAuthorizedForCourse(
            userMembership,
            content
          )
        )
      )) ? "Start" : "Upgrade";
    }

    return "Enroll in Course";
  }

  if (content.access === "paid" && !userMembership && !content.active_enrolment) {
    return "Enroll in Course";
  }

  if (content.access === "paid" && (userMembership && !((content.active_enrolment) && !(content.active_enrolment && content.active_enrolment.expired_at)) && (!userMembership.active_subscription || userMembership.active_subscription.plan.price === 0) && !(
    isAdmin(userMembership) ||
    (
      isModerator(userMembership) &&
      moderatorIsAuthorizedForCourse(
        userMembership,
        content
      )
    )
  ))) {
    return (userMembership && !userMembership.active_subscription) ? "Enroll in Course" : "Upgrade";
  }

  if (!((content.active_enrolment) && !(content.active_enrolment && content.active_enrolment.expired_at)) && content.access !== "priced") {
    if (!userMembership && content.access !== "free") {
      return "Enroll in Course";
    }
    return "Start";
  }

  if (
    !((content.active_enrolment) &&
      !(content.active_enrolment && content.active_enrolment.expired_at)) &&
    content.access === "priced" && !(
      isAdmin(userMembership) ||
      (
        isModerator(userMembership) &&
        moderatorIsAuthorizedForCourse(
          userMembership,
          content
        )
      )
    )
  ) {
    return 'Enroll';
  }

  if (!((content.active_enrolment) && !(content.active_enrolment && content.active_enrolment.expired_at)) && content.access === "priced" && (
    isAdmin(userMembership) ||
    (
      isModerator(userMembership) &&
      moderatorIsAuthorizedForCourse(
        userMembership,
        content
      )
    )
  )) {
    return "Start";
  }

  let contentsCount = isAdmin(userMembership) ? content.a_lessons_count : content.lessons_count;

  return contentsCount === content.completed_contents_count ? "View" : "Continue";
}

function getCourseButtonAction(actionText: string, content: any, history: any, userId: number, userMembership: any, openLockScreen: any, group: any) {
  const { 
    navigateToSinglePricingOptionPage, 
    shouldNavigateToPurchasePage,
  } = determinePurchaseNavigation(group, content, history, "courses");
  
  if (!userId) {
    if (content.lock_screen_active) {
      if (content.lock_screen_default_url && content.lock_screen_type === 'redirect') {
        window.location.assign(content.lock_screen_default_url);
        return;
      }
      openLockScreen();
      return;
    }

    if (actionText.startsWith("Enroll")) {
      // if (shouldNavigateToPurchasePage) {
      //  navigateToSinglePricingOptionPage();
      //  return;
      // }

      if (content.access === "paid") {
        if(
          group &&
          group.close_signup 
        ) {
          // TODO: Price decouple
          if (shouldNavigateToPurchasePage) {
            navigateToSinglePricingOptionPage();
            return;
          }
          history.push("/signup-disabled");
          return;
        }
        localStorage.setItem("post_purchase_redirect_link", window.location.pathname);
        setCookieValue("", true);
        if (shouldNavigateToPurchasePage) {
          history.push(`/plan-subscription?type=courses&id=${content.slug}`);
          return;
        }
        history.push("/plan-subscription");
        return;
      }

      if (content && content.access === "priced") {
        if (
          // content.access === "priced" &&
          content.price &&
          content.price.options &&
          content.price.options.length === 1
        ) {
          history.push(`/courses/${content.slug}/purchase?payment_option=${content.price.options[0].slug}`);
          return;
        }
        navigateToCoursePurchase(content, history);
        return;
      }
    }

    if (actionText === "Start" && content.access === "free") {
      localStorage.setItem("next_link", window.location.pathname);
    }

    if (
      content.access === "plan" &&
      content.authorized_plans.filter((plan: any) => !plan.archived_at).length === 0
    ) {
      // TODO: Price decouple
      if (shouldNavigateToPurchasePage) {
        navigateToSinglePricingOptionPage();
        return;
      }
      history.push("/plan-disabled");
      return;
    }

    if (
      content.access === "plan" &&
      content.authorized_plans.filter((plan: any) => !plan.archived_at).length === 1
    ) {
      if(
          group &&
          group.close_signup
        ) {
          // TODO: Price decouple
          if (shouldNavigateToPurchasePage) {
            navigateToSinglePricingOptionPage();
            return;
          }
          history.push("/signup-disabled");
          return;
        }
      
        if (shouldNavigateToPurchasePage) {
          history.push(`/plan-subscription?type=courses&id=${content.slug}`);
          return;
        }
 
      history.push(`/plan-subscription/checkout?plan=${content.authorized_plans.filter((plan: any) => !plan.archived_at)[0].uuid}&type=courses&id=${content.slug}`);
      return;
    }

    if (
      content.access === "plan" &&
      content.authorized_plans.filter((plan: any) => !plan.archived_at).length > 1
    ) {
      if(
          group &&
          group.close_signup
        ) {
          if (shouldNavigateToPurchasePage) {
            navigateToSinglePricingOptionPage();
            return;
          }
          history.push("/signup-disabled");
          return;
        }
      if (shouldNavigateToPurchasePage) {
          history.push(`/plan-subscription?type=courses&id=${content.slug}`);
          return;
        }
      history.push(`/plan-subscription?type=courses&id=${content.slug}`);
      return;
    }

    if(
          group &&
          group.close_signup
        ) {
          // TODO: Price decouple
          if (shouldNavigateToPurchasePage) {
            navigateToSinglePricingOptionPage();
            return;
          }
          history.push("/signup-disabled");
          return;
        }
    if (shouldNavigateToPurchasePage) {
          history.push(`/plan-subscription?type=courses&id=${content.slug}`);
          return;
        }
    history.push("/plan-subscription");

  }

  if (userId) {
    if (actionText === "Expired" && content.access === "priced") {
      if (
        content.price &&
        content.price.options &&
        content.price.options.length === 1
      ) {
        history.push(`/courses/${content.slug}/purchase?payment_option=${content.price.options[0].slug}`);
        return;
      }
      navigateToCoursePurchase(content, history);
      return;
    }
    if (actionText === "Expired") {
      toast('Access expired', {
        type: 'error',
        autoClose: 2000,
        position: "top-center",
        hideProgressBar: true,
        theme: "light"
      });
      return;
    }

    if (actionText === "Start" && (content.access === "free" || content.access === "plan" || content.access === "segment")) {
      startCourse(content, history);
      return;
    }

    if (actionText === "Restart") {
      startCourse(content, history);
      return;
    }

    if ((actionText === "Start" || actionText === "Upgrade" || actionText === "Expired" || actionText === "Enroll in Course") && content.access === "paid" && (!userMembership || !userMembership.active_subscription || userMembership.active_subscription.plan.price === 0) && !(
      isAdmin(userMembership) ||
      (
        isModerator(userMembership) &&
        moderatorIsAuthorizedForCourse(
          userMembership,
          content
        )
      )
    )) {
      if (content.lock_screen_active) {
        if (content.lock_screen_default_url && content.lock_screen_type === 'redirect') {
          window.location.assign(content.lock_screen_default_url);
          return;
        }
        openLockScreen();
        return;
      }
      // TODO: price decouple
      if (shouldNavigateToPurchasePage) {
        history.push(`/plan-subscription?type=courses&id=${content.slug}`);
        return;
      }

      history.push("/plan-subscription");
      return;
    }

    if (actionText === "Start" && content.access === "paid" && ((userMembership && userMembership.active_subscription && userMembership.active_subscription.plan.price > 0) || (
      isAdmin(userMembership) ||
      (
        isModerator(userMembership) &&
        moderatorIsAuthorizedForCourse(
          userMembership,
          content
        )
      )
    ))) {
      startCourse(content, history);
      return;
    }

    if ((actionText.startsWith("Enroll") && content.access !== "plan") || (actionText === "Expired" && content.access === "priced")) {
      if (
        content.access === "priced" &&
        content.price &&
        content.price.options &&
        content.price.options.length === 1
      ) {
        history.push(`/courses/${content.slug}/purchase?payment_option=${content.price.options[0].slug}`);
        return;
      }
      // TODO: price decouple
      if (shouldNavigateToPurchasePage && (content.access === "plan")) {
        history.push(`/plan-subscription?type=courses&id=${content.slug}`);
        return;
      }
      navigateToCoursePurchase(content, history);
      return;
    }

    if (actionText === "Start" && content.access === "priced") {
      startCourse(content, history);
      return;
    }

    if (actionText === "Upgrade" || actionText === "Enroll in Course" || actionText === "Expired") {
      if (content.lock_screen_active) {
        if (content.lock_screen_default_url && content.lock_screen_type === 'redirect') {
          window.location.assign(content.lock_screen_default_url);
          return;
        }
        openLockScreen();
        return;
      }

      if (
        content.access === "plan" &&
        content.authorized_plans &&
        content.authorized_plans.filter((plan: any) => !plan.archived_at).length === 0
      ) {
        // TODO: price decouple
        if(shouldNavigateToPurchasePage) {
          navigateToSinglePricingOptionPage();
          return;
        }
        window.location.replace("/plan-disabled");
        return;
      }
      if (
        content.access === "plan" &&
        content.authorized_plans &&
        content.authorized_plans.filter((plan: any) => !plan.archived_at).length === 1
      ) {
        // TODO: price decouple
      if (shouldNavigateToPurchasePage) {
        history.push(`/plan-subscription?type=courses&id=${content.slug}`);
        return;
      }
        const coursePlans = content.authorized_plans.filter((plan: any) => !plan.archived_at)
        window.location.replace(`/plan-subscription/checkout?plan=${coursePlans[0].uuid}&type=courses&id=${content.slug}`);
        return;
      }

      if (
        content.access === "plan" &&
        content.authorized_plans &&
        content.authorized_plans.filter((plan: any) => !plan.archived_at).length > 1
      ) {
        // TODO: price decouple
        if (shouldNavigateToPurchasePage) {
          history.push(`/plan-subscription?type=courses&id=${content.slug}`);
          return;
        }
        window.location.replace(`/plan-subscription?type=courses&id=${content.slug}`);
        return;
      }
      history.push("/plan-subscription");
      return;
    }
  }

  if (actionText === "Continue" || actionText === "View") {
    const contentId = (content.active_enrolment.last_content && content.lessons && content.lessons.includes(content.active_enrolment.last_content)) ? content.active_enrolment.last_content : content.lessons[0];
    history.push(`/courses/${content.slug}/lectures/${contentId}`);
    return;
  }
}

function startCourse(course: any, history: any) {
  // A cohort course is started from its course page, where the cohort is picked.
  if (isCohortCourse(course)) {
    history.push(`/courses/${course.slug}`);
    return;
  }

  if (course && course.contents_count === 0) {
    toast("This course doesn't have any content created.", {
      type: 'error',
      autoClose: 3000,

      position: "top-center",
      hideProgressBar: true,
      theme: "light"
    });
    return;
  }

  CourseService.getInstance()
    .startCourse(course.id)
    .then((response: any) => {
      if (!response || !response.data) {
        return;
      }
      const tempCourse = response.data
      if (!course.lessons){
        history.push(`/courses/${tempCourse.slug}/lectures/${tempCourse.lessons[0]}`)
        return
      }
      history.push(`/courses/${course.slug}/lectures/${course.lessons[0]}`);
    });
}

function getAccessLabel(content: any, userMembership?: any, group?: any) {
  if (content.active_access && userMembership && !isTeamMember(userMembership)) {
    return "Viewed";
  }
  if (content.active_enrolment_id &&
    userMembership &&
    !isTeamMember(userMembership)
  ) {
    return "Enrolled";
  }
  if (content.access === "priced") {
    return `${getCurrencyPrefix(content.currency)}${getCurrencySymbol(getResourceCurrency(content.currency, group && group.currency))}${content.price}`;
  }
  if (content.access === "paid") {
    return "Paid members";
  }
  return content.access;
}

function getCurrencyPrefix(currency: string): string {
  if (currency === "aud") {
    return "A";
  }
  if (currency === "cad") {
    return "C";
  }

  return "";
}

function sanitizeLibraryContentSlug(slug: string): string {
  return slug.replace(/\s+/g, '-').toLowerCase();
}

function sanitizeQuillValue(value: string) {
  if (value) {
    return value.replace(/<p class="ql-align-justify"><br><\/p>/g, "")
      .replace(/<p><br><\/p>/g, "");
  }
  return "";
}


const getRedirectBaseUrl = (group: any) => {
  let url = "";

  if (group) {
    if (group && group.custom_domain && group.custom_domain_status === "active" && appConfig.env !== "dev") {
      url = `https://${group.custom_domain}`;
    } else {
      url = `${appConfig.domain.protocol}://${group.subdomain}.${appConfig.domain.baseDomain}`;
    }
  } else {
    url = `${appConfig.domain.protocol}://${appConfig.domain.baseDomain}`;
  }

  if (appConfig.domain.port) {
    url = url + `:${appConfig.domain.port}`;
  }

  return url;

};

const getBaseDomain = () => {
  let domain = appConfig.domain.baseDomain;
  if (appConfig.env === "prod") {
    domain = `my.${domain}`;
  }
  domain = `${appConfig.domain.protocol}://${domain}`;
  if (appConfig.domain.port) {
    domain = `${domain}:${appConfig.domain.port}`;
  }

  return domain;
};

const getGroupSlugChannel = (path: string) => {
  const groupSlug = path.includes("/group/")
    ? (path = path.replace("/group/", ""))
    : path.replace("/channel/", "");
  const slug = groupSlug.split("/")[0];
  let channel: any = groupSlug.split("/")[1];

  if (channel) {
    channel = +channel;
  }
  return { slug, channel };
};

const convertWindowsLink = (link: any = "") => {
  let historyLink = link.replace(/^(?:\/group\/?:\/\/)?/i, "") || "";
  historyLink = historyLink.replace("/group/", "");
  return historyLink;
};

const getUrlFromString = (content: any = "") => {
  const urlRegex = /(http|https):\/\/[a-zA-Z0-9\-.]+\.[a-zA-Z]{2,3}(\/\S*)?/;
  const matchedUrl = content.match(urlRegex);
  return matchedUrl;
};

const getDomainFromUrl = (url: string) => {
  const matches = url.match(/^https?:\/\/([^?#]+)(?:[?#]|$)/i);
  return matches && matches[1];
};

function stringCapitalisation(name: string) {
  if (!name) {
    return '';
  }
  return `${name[0].toUpperCase()}${name.slice(1).toLocaleLowerCase()}`;
}

// White brand === "no brand set": collapse the theme to the null-branding shape
// so every theme-consuming style helper below applies the EXACT same neutral
// theming it would for a community with no custom_branding at all. The neutral
// look itself comes from the --p-* tokens (buildNeutralTokens) applied at the
// app root; these inline helpers simply step aside (return their no-custom
// result) instead of painting a white/brand value.
function effectiveTheme(theme: any): any {
  // "No usable brand color" === "no brand set": collapse to the null-branding
  // shape. Covers both a WHITE primaryColor and a MISSING one (e.g. a community
  // that picked "Chrome" but never set a color → custom_branding is a truthy
  // object with no primaryColor). Both must render the neutral/Classic default.
  if (theme && theme.custom && (!theme.custom.primaryColor || isWhiteColor(theme.custom.primaryColor))) {
    return { ...theme, custom: null };
  }
  return theme;
}

function getChannelLinksColor(group: any, channel: any, selectedChannel: any, theme: any) {
  theme = effectiveTheme(theme);
  if (channel && selectedChannel && theme && theme.custom) {
    const isSelected =
      selectedChannel === channel.slug &&
      !(
        group &&
        group.home_page_config &&
        group.home_page_config.status &&
        group.default_channel &&
        group.default_channel.slug === selectedChannel &&
        window.location.pathname === "/home"
      ) &&
      !window.location.pathname.split("/").includes("library") &&
      !window.location.pathname.split("/").includes("members") &&
      !window.location.pathname.split("/").includes("courses") &&
      // Don't highlight a channel named "Feed" on the top-level Feed page
      // (/feed or /group/<slug>/feed). Channel route is /channel/<slug>, so
      // only suppress when there's a "feed" segment but no "channel" segment.
      !(
        window.location.pathname.split("/").includes("feed") &&
        !window.location.pathname.split("/").includes("channel")
      );

    // Selected channel is a token-driven pill that matches the SOLID button
    // fill on the sidebar: the full brand color (--p-primary) with an
    // auto-contrast label/icon (--p-on-primary) so the active channel reads as
    // boldly as the buttons. A white brand color is treated exactly like "no
    // brand set" (effectiveTheme collapses theme.custom to null above), so it
    // never reaches here and instead falls back to the neutral grey CSS
    // `li.active` fill. Non-selected channels keep the default dark label.
    return isSelected
      ? { backgroundColor: 'var(--p-primary)', color: 'var(--p-on-primary)' }
      : {};
  }
  return {};
}


function getChannelHashTagColor(group: any, channel: any, selectedChannel: any, theme: any) {
  theme = effectiveTheme(theme);
  if (!theme || !theme.custom) {
    return undefined;
  }

  const isSelected =
    !(
      group &&
      group.home_page_config &&
      group.home_page_config.status &&
      group.default_channel &&
      group.default_channel.slug === selectedChannel &&
      window.location.pathname === "/home"
    ) &&
    !window.location.pathname.split("/").includes("library") &&
    !window.location.pathname.split("/").includes("members") &&
    !window.location.pathname.split("/").includes("courses") &&
    // Don't highlight a channel named "Feed" on the top-level Feed page
    // (/feed or /group/<slug>/feed); only the /channel/<slug> route should.
    !(
      window.location.pathname.split("/").includes("feed") &&
      !window.location.pathname.split("/").includes("channel")
    ) &&
    selectedChannel === channel.slug;

  // The selected channel now sits on a SOLID brand fill (see
  // getChannelLinksColor), so its hashtag must take the auto-contrast on-brand
  // color — not the brand color itself, which would vanish brand-on-brand.
  // Returned as a hex; applied as an SVG fill attribute where var() would not
  // resolve. White is already collapsed to the null-branding theme above, so a
  // real brand color is the only value that can reach here.
  return isSelected ? getAutoFontColor(theme.custom.primaryColor, theme.custom) : undefined;
}

function getSidebarActionLinkStyle(isActive: boolean, theme: any) {
  theme = effectiveTheme(theme);
  if (theme && theme.custom) {
    // The active nav item is a token-driven pill that matches the SOLID button
    // fill: the full brand color (--p-primary) with an auto-contrast label/icon
    // (--p-on-primary), so the active item reads as boldly as the buttons. A
    // white brand color is treated exactly like "no brand set" (effectiveTheme
    // collapses theme.custom to null above) — it never reaches here and instead
    // falls back to the neutral grey CSS `active-courses` fill, so the legacy
    // gradient/darker/custom-sidebar/selected fields are intentionally ignored.
    // Inactive items keep the default dark label (hover fill comes from CSS).
    return isActive
      ? { color: 'var(--p-on-primary)', backgroundColor: 'var(--p-primary)' }
      : {};
  }
  return {};
}

function getBlockButtonStyle(theme: any, onlyBackground?: boolean) {
  theme = effectiveTheme(theme);
  const style: { [k: string]: any } = {};

  if (theme && theme.custom) {
    style.color = !onlyBackground ? getAutoFontColor(theme.custom.primaryColor, theme.custom) : '#ffffff';
    style.border = "none";
    if (theme.custom.primaryColor) style.backgroundColor = theme.custom.primaryColor;
  }
  return style;
}

function getBlockInverseButtonStyle(theme: any, hover: boolean = false) {
  theme = effectiveTheme(theme);
  const style: { [k: string]: any } = {};
  if (theme && theme.custom && theme.custom.primaryColor) {
    const autoColor = getAutoFontColor(theme.custom.primaryColor, theme.custom);
    style.color = autoColor;
    style.border = `1px solid ${autoColor}`;
    if ((hover || !hover)) {
      style.backgroundColor = theme.custom.primaryColor;
    }
  }
  return style;
}

function getTextButtonStyle(theme: any) {
  theme = effectiveTheme(theme);
  const style: { [k: string]: any } = {};
  if (theme && theme.custom && theme.custom.primaryColor) {
    style.color = theme.custom.primaryColor;
  }
  return style;
}

function getOutlineButtonStyle(theme: any, hover: boolean = false, disabled: boolean = false) {
  theme = effectiveTheme(theme);
  if (theme && theme.custom && theme.custom.primaryColor) {
    if (hover && !disabled) {
      const autoColor = getAutoFontColor(theme.custom.primaryColor, theme.custom);
      return {
        color: autoColor,
        backgroundColor: theme.custom.primaryColor,
        border: `1px solid ${autoColor}`
      };
    }
    if (hover && disabled) {
      return {
        color: theme.custom.primaryColor,
        backgroundColor: "white",
        border: `1px solid ${theme.custom.primaryColor}`
      };
    }
    return { color: theme.custom.primaryColor, border: `1px solid ${theme.custom.primaryColor}` };
  }
  return {};
}

function getCoursePriceLabelStyle(theme: any) {
  theme = effectiveTheme(theme);
  const style: { [k: string]: any } = {};
  if (theme && theme.custom && theme.custom.primaryColor) {
    style.border = `1px solid ${theme.custom.primaryColor}`;
    style.backgroundColor = "white";
    style.fontWeight = "bold";
    style.color = theme.custom.primaryColor;
  }
  return style;
}

function customThemeSet(theme: any) {
  theme = effectiveTheme(theme);
  return theme && theme.custom && theme.custom.primaryColor && !isWhiteColor(theme.custom.primaryColor);
}

function customGroupThemeSet(group: any) {
  return group && group.custom_branding && group.custom_branding.primaryColor && !isWhiteColor(group.custom_branding.primaryColor);
}

function secondsToContentDuration(secondsInput: any, simplified?: boolean) {
  if (!secondsInput) {
    return "";
  }
  const sec_num = parseInt(secondsInput);
  const hours = Math.floor(sec_num / 3600);
  let minutes = Math.floor((sec_num - (hours * 3600)) / 60);
  let seconds = sec_num - (hours * 3600) - (minutes * 60);
  let duration = "";
  let timeName = simplified ? "m" : "minutes";

  if (hours > 0) {
    duration += `${hours}:`;
    timeName = simplified ? "h" : "hours";
  }

  const paddedSeconds = seconds.toString().padStart(2, "0");
  const displayMinutes = hours > 0 ? minutes.toString().padStart(2, "0") : `${minutes}`;

  if (simplified) {
    duration += `${displayMinutes}:${paddedSeconds}${timeName}`;
  } else {
    duration += `${displayMinutes}:${paddedSeconds} ${timeName}`;
  }

  return duration;
}

function formatDuration(duration: any) {
  const splited = duration.split(":");
  if (parseInt(splited[0]) !== 0 && parseInt(splited[1]) === 0) {
    return `${parseInt(splited[0])}h`;
  }
  if (parseInt(splited[0]) !== 0 && parseInt(splited[1]) !== 0) {
    return `${parseInt(splited[0])}h ${parseInt(splited[1])}m`;
  }
  if (parseInt(splited[0]) === 0 && parseInt(splited[1]) !== 0) {
    return `${parseInt(splited[1])}m`;
  }
  if (
    parseInt(splited[0]) === 0 &&
    parseInt(splited[1]) === 0 &&
    parseInt(splited[2]) !== 0
  ) {
    return `${parseInt(splited[2])}s`;
  }
  if (
    parseInt(splited[0]) === 0 &&
    parseInt(splited[1]) === 0 &&
    parseInt(splited[2]) === 0
  ) {
    return `0m`;
  }
}

function formatSecondsDuration(seconds: number) {
  const h = Math.floor(seconds / 3600); // Calculate hours
  const m = Math.floor((seconds % 3600) / 60); // Calculate remaining minutes
  const s = seconds % 60; // Calculate remaining seconds

  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')} h`; // Format as hours and minutes
  } else if (m > 0) {
    return `${m}:${s.toString().padStart(2, '0')} min`; // Format as minutes and seconds
  } else {
    return `${s} sec`; // Format as seconds
  }
}

function prepareGroupPlans(plans: any) {
  let _tempPlans: any = [];
  const filteredPlans = plans.filter(function (plan: any) {
    return !plan.archived_at;
  });

  _tempPlans = filteredPlans.map(function (plan: any) {
    if (plan.name === "one-time") {
      return { ...plan, rank: 1 };
    }
    if (plan.name === "yearly") {
      return { ...plan, rank: 2 };
    }
    if (plan.name === "quarterly") {
      return { ...plan, rank: 3 };
    }
    if (plan.name === "monthly") {
      return { ...plan, rank: 4 };
    }
    if (plan.name === "free") {
      return { ...plan, rank: 5 };
    }
    if (plan.name === "Daily") {
      return { ...plan, rank: 6 };
    }
    return null;
  });

  _tempPlans.sort((a: any, b: any) => (a.rank > b.rank ? 1 : -1));
  return _tempPlans;
}

function formatGroupPlans(plans: any) {
  const filteredPlans = plans.filter(function (plan: any) {
    return !plan.archived_at;
  });

  filteredPlans.sort((a: any, b: any) => (a.rank > b.rank ? 1 : -1));
  return filteredPlans;
}

function filterNumbers(data: any) {
  return data.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
}

function blobToFile(compressedFile: any, originalFile: any) {
  return new File([compressedFile], originalFile.name);
}

function getBillingFrequency(plan: any) {
  if (plan.interval === "one_time") return null;
  if (plan.interval === "month" && plan.interval_count === 3) {
    return "/quarterly";
  }
  if (plan.interval === "month") return "/monthly";
  if (plan.interval === "year") return "/yearly";
}

function getPlanInterval(plan: any) {
  let plan_interval = "";

  if (plan.interval === "one_time") {
    plan_interval = " / one-time";
  } else if (plan.interval === null && plan.interval_count === null && plan.price === 0) {
    plan_interval = " / month";
  } else {
    if (plan.interval === " month" && plan.interval_count === 3) {
      plan_interval = " /quarter";
    } else if (plan.interval_count === 1) {
      plan_interval = " / " + plan.interval;
    } else {
      plan_interval = " / every " + plan.interval_count + " " + plan.interval + "s";
    }
  }

  return plan_interval;
}

function getPlanIntervally(plan: any) {
  let plan_interval = "";

  if (plan.interval === "one_time") {
    plan_interval = " one-time";
  } else if (plan.interval === null && plan.interval_count === null && plan.price === 0) {
    plan_interval = " monthly";
  } else {
    if (plan.interval === "month" && plan.interval_count === 1) {
      plan_interval = " monthly";
    } else if (plan.interval === "month" && plan.interval_count === 3) {
      plan_interval = " quarterly";
    } else if (plan.interval === "year") {
      plan_interval = " yearly";
    } else if (plan.interval_count === 1) {
      plan_interval = " per " + plan.interval;
    } else {
      plan_interval = " every " + plan.interval_count + " " + plan.interval + "s";
    }
  }

  return plan_interval;
}

function getPlanCycle(plan: any) {
  let plan_interval = "";

  if (plan.interval === "one_time") {
    plan_interval = "one-time";
  } else if (plan.interval === null && plan.interval_count === null && plan.price === 0) {
    plan_interval = "every month";
  } else {
    if (plan.interval === " month" && plan.interval_count === 3) {
      plan_interval = "every quarter";
    } else if (plan.interval_count === 1) {
      plan_interval = " / " + plan.interval;
    } else {
      plan_interval = "every " + plan.interval_count + " " + plan.interval + "s";
    }
  }

  return plan_interval;
}

function toTitleCase(str: any) {
  if (str) {
    return str.replace(
      /\w\S*/g,
      function (txt: any) {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
      }
    );
  }
  return "";
}

function toLowerCase(str: any) {
  let lcString = str.toLowerCase();
  return lcString;
}

function mBToGb(sizeInMB: any, decimals = 2) {
  var sizeInGB = (sizeInMB / (1000)).toFixed(decimals) + "GB";

  return sizeInGB;
}

function getDateDiffInDays(date: any) {
  const tz = momentTimezone.tz.guess();
  const today = momentTimezone.tz(moment(), tz);
  const _date = momentTimezone.tz(date, tz);

  const days = _date.diff(today, "days");
  return days;
}

function getDateDiffInHours(date: any) {
  const tz = momentTimezone.tz.guess();
  const today = momentTimezone.tz(moment(), tz);
  const _date = momentTimezone.tz(date, tz);

  const hours = _date.diff(today, "hours");
  return hours;
}

function isTrialPast(group: any) {
  const tz = momentTimezone.tz.guess();
  const today = momentTimezone.tz(moment(), tz);
  const trialDate = momentTimezone.tz(group.trial_ends_at, tz);

  const dateDiff = trialDate.diff(today);
  if (dateDiff < 0 || !group.active_subscription) {
    return true;
  }
  return false;
}
function getAvailableCurrencies() {
  return [
    {
      name: "US Dollar",
      value: "USD"
    },
    {
      name: "Euro",
      value: "EUR"
    },
    {
      name: "Pound Sterling",
      value: "GBP"
    },
    {
      name: "Australian Dollar",
      value: "AUD"
    },
    {
      name: "Canadian Dollar",
      value: "CAD"
    },
    {
      name: "Swedish Krona",
      value: "SEK"
    },
    {
      name: "Danish Krone",
      value: "DKK"
    },
    {
      name: "Norwegian Krone",
      value: "NOK"
    },
    {
      name: "Swiss Franc",
      value: "CHF"
    },
    {
      name: "Brazilian Real",
      value: "BRL"
    },
    {
      name: "South African Rand",
      value: "ZAR"
    },
    {
      name: "Czech Koruna",
      value: "CZK"
    },
    {
      name: "Hungarian Forint",
      value: "HUF"
    },
    {
      name: "Indian Rupee",
      value: "INR"
    },
    {
      name: "Polish Złoty",
      value: "PLN"
    },
  ];
}

function getCurrencySymbol(currency: any) {
  let currencySymbol: any;
  if (currency && currency !== "NULL" && currency !== "null") {
    currencySymbol = getSymbolFromCurrency(currency);
    if (currencySymbol) {
      return currencySymbol;
    } else {
      return currency.toUpperCase();
    }
  }

  return "$";
}

function getResourceCurrency(currency: any, groupCurrency: any) {
  if (currency && currency !== "NULL" && currency !== "null") {
    return currency.toUpperCase();
  }

  if (groupCurrency && groupCurrency !== "NULL") {
    return groupCurrency.toUpperCase();
  }

  return "USD";
}

function getTimeFromDate(date: any, formattingStyle: any) {
  if (date) {
    // return moment.utc(moment(date)).format(formattingStyle);
    return moment(moment(date)).utc().format(formattingStyle);
  }
  return "";
}

function getFormattedDateFromDate(date: any, formattingStyle: any) {

  if (date) {
    // return moment.utc(moment(date)).format(formattingStyle);
    return moment(moment(date)).utc().format(formattingStyle);
  }
  return "";
}

function getDateFromString(dateString: any, UTC?: boolean) {
  if (dateString) {
    if (UTC) {
      return new Date(dateString + " UTC");
    } else {
      return new Date(dateString);
    }
  }
  return null;
}

function getTimezoneDateFromString(dateString: any, timezone?: any) {
  if (dateString) {
    return new Date(dateString + `${timezone}`);
  }
  return null;
}

function getGroupPlan(group: any) {
  if (group && group.active_subscription && group.active_subscription.plan) {
    return group.active_subscription.plan;
  }
  return {};
}

// A course the plan won't let you publish can't sell either, so its checkout
// pricing has to stay inactive. active_courses wins when the plan sets it,
// otherwise fall back to the courses limit; -1 or missing means unlimited.
export function planBlocksCourseActivation(group: any, publishedCourses: any) {
  const plan = getGroupPlan(group);
  const limit = plan && plan.active_courses !== null && plan.active_courses !== undefined
    ? plan.active_courses
    : plan && plan.courses;

  if (limit === null || limit === undefined || limit === -1) {
    return false;
  }

  return limit === 0 || (publishedCourses || 0) >= limit;
}

// How many files one resource-library item may hold. Returns null for
// unlimited so callers can tell "no ceiling" apart from a ceiling of 0.
export function getLibraryResourceFileLimit(group: any): number | null {
  const plan = getGroupPlan(group);
  const limit = plan && plan.library_resource_files;

  if (limit === null || limit === undefined || limit === -1) {
    return null;
  }

  return limit;
}

export function planBlocksLibraryResourceFile(group: any, currentFileCount: number): boolean {
  const limit = getLibraryResourceFileLimit(group);

  if (limit === null) {
    return false;
  }

  return limit === 0 || (currentFileCount || 0) >= limit;
}

function isGroupOnPlan(group: any) {
  if (group && group.active_subscription) {
    return true;
  }
  return false;
}

function isGroupOnFreePlan(group: any) {
  const plan = getGroupPlan(group);
  if (plan && plan.price !== null && plan.price !== undefined && Number(plan.price) === 0) {
    return true;
  }
  return false;
}

function isGroupOnDemoPlan(group: any) {
  const plan = getGroupPlan(group);
  return Boolean(plan && plan.name && `${plan.name}`.toLowerCase() === 'demo');
}

// True until the community has actually chosen something to be on.
function groupHasNotChosenPlan(group: any) {
  return Boolean(group && (!group.active_subscription || isGroupOnDemoPlan(group)));
}

function isGroupDataReady(group: any) {
  return Boolean(
    group &&
    group.user_membership &&
    group.active_subscription &&
    group.active_subscription.plan
  );
}

function updateArrayObject(changedObject: any, arrayItems: any, changedAttributes: any) {

  arrayItems.forEach((_object: any, index: any) => {
    if (_object.id === changedObject.id) {
      changedAttributes.forEach((attribute: any, idx: any) => {
        arrayItems[index][attribute] = changedObject[attribute];
      });
    }
  });

  return arrayItems;
}

function getDateInCurrentTimezone(date = new Date()) {
  const tz = momentTimezone.tz.guess();
  // const dateInUtc = moment.utc(moment(date));

  // return momentTimezone.tz(dateInUtc, tz);
  return momentTimezone(date, "YYYY-MM-DD HH:mm:ss").tz(tz);
}

function getDateInGivenTimezone(timezone: any, date?: any) {
  // const dateInUtc = moment.utc(moment(date));

  // return momentTimezone.tz(dateInUtc, timezone);
  // return momentTimezone(date, "YYYY-MM-DD HH:mm:ss A zz").tz(timezone);
  return momentTimezone(date, "YYYY-MM-DD HH:mm:ss").tz(timezone);
}

function getUserTimezone(abbreviation?: any) {
  let timezone;

  // Try to use Intl API if available (modern browsers)
  if (
    typeof Intl !== "undefined" &&
    Intl.DateTimeFormat &&
    Intl.DateTimeFormat().resolvedOptions
  ) {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  // Fallback to moment-timezone guess
  if (!timezone) {
    timezone = moment.tz.guess();
  }

  // Normalize "Etc/GMT±X" zones (e.g. "Etc/GMT+6")
  if (timezone && timezone.indexOf("Etc/GMT") === 0) {
    const offsetMinutes = moment().utcOffset();

    // Try to find a region with the same offset
    const names = moment.tz.names();
    for (let i = 0; i < names.length; i++) {
      const name = names[i];
      if (moment.tz(name).utcOffset() === offsetMinutes) {
        timezone = name;
        break;
      }
    }

    if (!timezone) timezone = "UTC";
  }

  // Return abbreviation (like "EAT") if requested
  if (abbreviation) {
    return moment().tz(timezone).format("z");
  }

  // Otherwise return full timezone name (like "Africa/Nairobi")
  return timezone;
}

function getTimezoneAbbreviation(timezone: any) {
  if (timezone) {
    return moment().tz(timezone).format("z");
  }
  return "";
}

function getUserTimezoneAbbreviation(timezone?: string) {
  let targetTimezone = timezone ? timezone : moment.tz.guess();
  const abbreviation = moment().tz(targetTimezone).zoneAbbr();

  // Check if the abbreviation consists only of letters
  if (/^[A-Za-z]+$/.test(abbreviation)) {
    return abbreviation;
  } else {
    return 'GMT';
  }
}

function getUserPlan(user: any) {
  if (user && user.active_subscription && user.active_subscription.plan) {
    return user.active_subscription.plan;
  }
  return {};
}

function getCommunityTimezone(group: any) {
  if (group) {
    if (group.timezone) {
      return group.timezone.value;
    } else {
      return getUserTimezone();
    }
  }
}

function getDateObjectFromGivenTimezone(dateObject: any, timezone: any, dateString?: any) {
  if (dateObject) {
    return eventsUtils.getDateFromDatestring(dateObject.toLocaleString("en-US", { timeZone: timezone }));
  }

  if (dateString) {
    return moment(moment(dateString).toDate().toLocaleString("en-US", { timeZone: timezone })).toDate();
  }
  return new Date();
}

function convertDateStringToUsertimezoneFormat(dateString: string, format: string): string;
function convertDateStringToUsertimezoneFormat(dateString: string, format?: string): string | moment.Moment;
function convertDateStringToUsertimezoneFormat(dateString: string, format?: string) {
  const userTimezone = moment.tz.guess();
  const userTimezoneOffset = new Date().getTimezoneOffset();

  const date = moment(getDateObjectFromGivenTimezone(null, userTimezone, dateString)).subtract(userTimezoneOffset / 60, "hours");

  if (format) {
    return date.format(format);
  }

  return date;
}

function changeTimezone(date: any, ianatz: any) {

  // suppose the date is 12:00 UTC
  var invdate = new Date(date.toLocaleString("en-US", {
    timeZone: ianatz
  }));

  // then invdate will be 07:00 in Toronto
  // and the diff is 5 hours
  var diff = date.getTime() - invdate.getTime();

  // so 12:00 in Toronto is 17:00 UTC
  return new Date(date.getTime() - diff); // needs to substract

}

function getTimezoneUTCOffset(timezone: any) {
  return momentTimezone.tz(timezone).utcOffset();
}

function getUTCDateFromTimezoneDate(date: any, timezoneOffset: any) {
  return moment(getDateInGivenTimezone("Etc/GMT", date)).utcOffset(-timezoneOffset);
  // return moment(moment(date).format("YYYY-MM-DD HH:mm:ss")).utcOffset(-timezoneOffset);
}

function getReadableTimezoneOffset(timezone: any) {
  const timezoneOffset = getTimezoneUTCOffset(timezone) / 60;
  const offsetString = timezoneOffset.toString();
  // const timezoneAbbreviation = getTimezoneAbbreviation(timezone);

  // This is to take care of those edge cases where moment is not able to return the abbreviations
  // for some timezones but rather returns offsets.
  // if (!timezoneAbbreviation.includes("-") && !timezoneAbbreviation.includes("+")) {
  if (timezoneOffset > 0) {
    return `${timezoneOffset >= 10 ? '+' : '+0'}${timezoneOffset}`;
  } else {
    if (timezoneOffset === 0) {
      return `+0${timezoneOffset}`;
    } else {
      return `${timezoneOffset <= -10 ? '-' : '-0'}${offsetString.split("-")[1]}`;
    }
  }
  // }
}

function getImageExtension(imageFile: any) {
  if (imageFile) {
    var imageName = imageFile.name;
    var dotIndex = imageName.lastIndexOf(".");
    return imageName.substring(dotIndex + 1);
  }
  return "jpeg";
}

function isQuillEmpty(value: string) {
  if (value.replace(/<(.|\n)*?>/g, "").trim().length === 0 && !value.includes("<img")) {
    return true;
  }
  return false;
}

function hexToRgb(hex: string) {
  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

function formatLessonDescription(value: any) {
  if (value) {
    return value.replace(/(?:\r|\n|\r\n)/g, "<br>")
      .replace(/(\b(https?|ftp|file):\/\/[\-A-Z0-9+&@#\/%?=~_|!:,.;]*[\-A-Z09+&@#\/%=~_|])/img, "<a href=\"$1\">$1</a>");
  }
}

function sanitizeLessonDescription(value: any) {
  if (value) {
    return value.replace(/<br>/g, "\n").replace(/<\/?[^>]+(>|$)/g, "");
  }
}

function getMemberType(roleId: number) {
  switch (roleId) {
    case 1:
      return "Owner";
    case 2:
      return "Admin";
    case 3:
      return "Moderator";
    case 4:
      return "Member";
    default:
      return "Guest";
  }
};

function stripHTML(value: string) {
  if (value) {
    return value.replace(/(<([^>]+)>)/ig, "").replace(/(?:\r|\n|\r\n)/g, "");
  }

  return "";
}

function stripHTMLAndKeepLineBreaks(value: string) {
  if (value) {
    return value
      // Replace <br> with \n
      .replace(/<br\s*\/?>/gi, "\n")
      // Replace <p><br></p> with \n
      .replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, "\n")
      // Remove all remaining HTML tags
      .replace(/<[^>]+>/g, "")
      // Trim extra spaces around newlines
      .trim();
  }
  return "";
}

function isLink(value: string) {
  if (value) {
    const linkRegex = new RegExp(/(\b(https?|ftp|file):\/\/[\-A-Z0-9+&@#\/%?=~_|!:,.;]*[\-A-Z09+&@#\/%=~_|])/img);
    return linkRegex.test(value);
  }

  return false;
}

function getVideoTypeAndIdFromUrl(video_url: any) {
  let videoType: any = "";
  let videoId: any = "";

  if (video_url && video_url.includes("you")) {
    videoType = "youtube";
    videoId = getYouTubeVideoId(video_url);

    // let urlParse;
    // const videoURL = video_url.split("/");
    // videoURL.map((part: any, index: any) => {
    //   if (part.includes("watch")) {
    //     urlParse = part.split("=")[1];
    //     videoId = urlParse;
    //   } else {
    //     if (index === 3) {
    //       videoId = part;
    //     }
    //   }
    // });
  }

  if (video_url && video_url.includes("vimeo")) {
    videoType = "vimeo";
    // const videoURL = video_url.split("/");
    videoId = video_url;
  }

  if (video_url && video_url.includes("wistia")) {
    videoType = "wistia";
    const videoURL = video_url.split("/");
    videoId = videoURL[4];
  }

  // if (video_url && video_url.includes("loom")) {
  //   videoType = "loom";
  //   videoId = video_url;
  // }

  return { video_type: videoType, video_id: videoId };
}

function validateFileName(file: any) {
  if (!file) {
    return false;
  }
  const MAX_FILE_NAME_CHARACTER_LIMIT: number = 200;

  if (file && file.name && file.name.length > MAX_FILE_NAME_CHARACTER_LIMIT) {
    toast(`File name exceeds ${MAX_FILE_NAME_CHARACTER_LIMIT} characters`, {
      type: 'warning',
      autoClose: 3000,
      position: "top-center",
      hideProgressBar: true,
      theme: "light"
    });
    return false;
  }

  return true;
}

function objectToQueryString(object: any) {
  return Object.keys(object).map(key => key + '=' + object[key]).join('&');
}

function useNbeaFlow(group: any) {
  if (!group) {
    return false;
  }

  return group.sso_enabled && group.subdomain === "nbea";
}

function OAuthActivated(group: any) {
  if (!group) {
    return false;
  }

  return (
    group.sso_enabled &&
    group.sso_type === SSOMethod.OAUTH &&
    group.subdomain !== "nbea"
  )
}

function customSignUpIsSetUp(group: any) {
  if (!group) {
    return false;
  }

  return (
    group.sso_enabled &&
    group.sso_type === SSOMethod.OAUTH &&
    group.oauth_sso &&
    group.oauth_sso.allow_custom_signup &&
    group.oauth_sso.custom_signup_url
  )
}

function userIsOnResourcePlan(userMembership: any, resourcePlans: Array<any>) {
  if (
    !userMembership ||
    !userMembership.active_subscription ||
    !userMembership.active_subscription.plan ||
    !resourcePlans
  ) {
    return false;
  }

  let planIsAuthorized: boolean = false;
  resourcePlans.forEach((plan: any) => {
    if (plan.id === userMembership.active_subscription.plan.id) {
      planIsAuthorized = true;
    }
  });
  return planIsAuthorized;
}

function userIsOnResourseSegment(userMembership: any, resourceSegments: Array<any>) {
  if (
    !userMembership ||
    !userMembership.segments ||
    !resourceSegments
  ) {
    return false;
  }

  let userIsOnSegment = false;
  resourceSegments.forEach((resourceSegment: any) => {
    userMembership.segments.forEach((userSegment: any) => {
      if (userSegment.id === resourceSegment.id) {
        userIsOnSegment = true
      }
    });
  });

  return userIsOnSegment;
}

function getLockScreenDetails(resource: any) {
  if (resource) {
    const {
      lock_screen_active,
      lock_screen_btn_label,
      lock_screen_btn_url,
      lock_screen_instructions,
      lock_screen_title,
      lock_screen_default_url,
      lock_screen_type
    } = resource;

    return {
      lock_screen_active: !!lock_screen_active,
      lock_screen_btn_label: lock_screen_btn_label ? lock_screen_btn_label : '',
      lock_screen_btn_url: lock_screen_btn_url ? lock_screen_btn_url : '',
      lock_screen_instructions: lock_screen_instructions ? lock_screen_instructions : '',
      lock_screen_title: lock_screen_title,
      lock_screen_default_url: lock_screen_default_url,
      lock_screen_type: lock_screen_type
    }
  }

  return {
    lock_screen_active: false,
    lock_screen_btn_label: '',
    lock_screen_btn_url: '',
    lock_screen_instructions: '',
  }
}

export function getSizeValues(size?: TSize, isLayout = false): ISizeValue {
  switch (size ? size : "xs") {
    case "xs":
      return {
        height: isLayout ? "10rem" : "1rem",
        width: isLayout ? "10rem" : "1rem"
      }
    case "sm":
      return {
        height: isLayout ? "20rem" : "2rem",
        width: isLayout ? "20rem" : "2rem"
      }
    case "md":
      return {
        height: isLayout ? "30rem" : "3rem",
        width: isLayout ? "30rem" : "3rem"
      }
    case "lg":
      return {
        height: isLayout ? "40rem" : "4rem",
        width: isLayout ? "40rem" : "4rem"
      }
    case "xl":
      return {
        height: isLayout ? "50rem" : "5rem",
        width: isLayout ? "50rem" : "5rem"
      }
    case "2xl":
      return {
        height: isLayout ? "60rem" : "6rem",
        width: isLayout ? "60rem" : "6rem"
      }
    case "3xl":
      return {
        height: isLayout ? "70rem" : "7rem",
        width: isLayout ? "70rem" : "7rem"
      }

    case "4xl":
      return {
        height: isLayout ? "80rem" : "8rem",
        width: isLayout ? "80rem" : "8rem"
      }

    case "5xl":
      return {
        height: isLayout ? "90rem" : "9rem",
        width: isLayout ? "90rem" : "9rem"
      }

    case "6xl":
      return {
        height: isLayout ? "100rem" : "10rem",
        width: isLayout ? "100rem" : "10rem"
      }

    case "7xl":
      return {
        height: isLayout ? "110rem" : "11rem",
        width: isLayout ? "110rem" : "11rem"
      }

    case "fullscreen":
      return {
        height: "100vh",
        width: "100vh"
      }

    default:
      return {
        height: isLayout ? "50rem" : "5rem",
        width: isLayout ? "50rem" : "5rem"
      }
  }
}

const getReorderedResourcesUsingDraggable = (result: any, resourcesToBeOrdered: Array<any>) => {
  //user dropped outside context
  if (!result.destination) {
    return resourcesToBeOrdered;
  }

  //user dropped to previous location
  if (
    result.source.droppableId === result.destination.droppableId &&
    result.source.index === result.destination.index
  ) {
    return resourcesToBeOrdered;
  }

  //find the dragged resource
  let draggedResource: any;

  resourcesToBeOrdered.forEach((resource: any) => {
    if (`${resource.id}` === `${result.draggableId}`) {
      draggedResource = resource;
    }
  })

  const reorderedResources = Array.from(resourcesToBeOrdered);

  if (draggedResource) {
    reorderedResources.splice(result.source.index, 1);
    reorderedResources.splice(result.destination.index, 0, draggedResource);
  }

  return reorderedResources;
}

function toSentenceCase(str: string) {
  let _tmp = str.toLowerCase();
  let result = _tmp
    .replace(/[a-z]/i, function (letter) {
      return letter.toUpperCase();
    })
    .trim();

  return result;
}

export function removeTags(str: any) {
  if ((str === null) || (str === ''))
    return false;
  else
    str = str.toString();

  // Regular expression to identify HTML tags in
  // the input string. Replacing the identified
  // HTML tag with a null string.
  return str.replace(/(<([^>]+)>)/ig, '');
}

export function decodeHtml(html: string) {
  var txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
}

export function removeFormdataExtraNewLines(str: any) {
  if ((str === null) || (str === ''))
    return false;
  else
    str = str.toString();

  return str.replace(/\/r/i, '');
}

function isNumeric(value: any) {
  return !isNaN(value)
}

function getGroupSubdomainFromSlug(slug: string) {
  let subdomain;

  if (!slug) {
    return null;
  }

  const sub = slug.split("://")[1];

  if (sub) {
    subdomain = sub.split(".")[0];
  }

  return subdomain;
}

function getListFromEnum(data: any): any[] {
  const list: any[] = [];

  Object.keys(data).forEach((key) => {
    list.push(sentenceCase(data[key]));
  });

  return list;
}

function sentenceCase(str: string) {
  let _tmp = str.toLowerCase();
  let result = _tmp
    .replace(/[a-z]/i, function (letter) {
      return letter.toUpperCase();
    })
    .trim();

  return result;
}

const validateEmail = (email: string) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
};

const getUserSubscriptionTrialEndDate = (plan: any) => {
  if (plan && plan.trial_days && plan.trial_days > 0) {
    return moment(new Date()).add(plan.trial_days, "days").toDate();
  }
}

const userTrialEndDate = (group: any) => {
  return (group && group.user_membership && group.user_membership.active_subscription && group.user_membership.active_subscription.trial_ends_at);
}

const getUserSubscriptionStartDate = (group: any) => {
  return (group && group.user_membership && group.user_membership.active_subscription && group.user_membership.active_subscription.created_at);
}

const userIsOnTrial = (group: any) => {
  return (group && group.user_membership && group.user_membership.active_subscription && group.user_membership.active_subscription.on_trial);
}

const getTrialDaysRemaining = (group: any) => {
  const now = moment();

  if (group.user_membership) {
    const trialEndsDate = moment(userTrialEndDate(group));

    if (trialEndsDate) {
      return trialEndsDate.diff(now, "days");
    }
  }
  return 0;
}

const getTrialDaysUsed = (group: any) => {
  const now = moment();

  if (group.user_membership) {
    const subscriptionStartDate = moment(getUserSubscriptionStartDate(group));

    if (subscriptionStartDate) {
      return now.diff(subscriptionStartDate, "days");
    }
  }
  return 0;
}

const getProratedTrialDays = (group: any, plan: any) => {
  const trialDaysUsed = getTrialDaysUsed(group);

  if (group.user_membership) {
    if (plan && trialDaysUsed) {
      const proratedTrialDays = plan.trial_days - trialDaysUsed;

      if (proratedTrialDays > 0) {
        return proratedTrialDays;
      }
    }

    return 0;
  }

  return plan.trial_days;

}

const isSubscriptionPaymentFailedOrPastDue = (group: any) => {
  return group && group.user_membership &&
    (
      group.user_membership.subscription_status === USER_SUBSCRIPTION_STATUS.PAST_DUE ||
      (
        group.user_membership.active_subscription &&
        group.user_membership.active_subscription.stripe_status === USER_SUBSCRIPTION_STATUS.PAST_DUE
      )
    ) &&
    !isOwner(group.user_membership) &&
    !isAdmin(group.user_membership) &&
    !isModerator(group.user_membership) &&
    !isMembershipPendingApproval(group.user_membership)
}

const isSubscriptionPaymentCanceled = (group: any) => {
  return group && group.user_membership &&
    !group.user_membership.active_subscription &&
    !group.user_membership.temp_plan &&
    !isOwner(group.user_membership) &&
    !isAdmin(group.user_membership) &&
    !isModerator(group.user_membership) &&
    !isMembershipPendingApproval(group.user_membership)
}

const isSubscriptionPaymentCanceledAndHasTemp = (group: any) => {
  return group && group.user_membership &&
    !group.user_membership.active_subscription &&
    group.user_membership.temp_plan &&
    !group.user_membership.previous_plan &&
    !isOwner(group.user_membership) &&
    !isAdmin(group.user_membership) &&
    !isModerator(group.user_membership) &&
    !isMembershipPendingApproval(group.user_membership)
}

const subscriptionHasActivePreviousPlan = (group: any) => {
  return (
    group &&
    group.user_membership &&
    group.user_membership.previous_plan &&
    !group.user_membership.previous_plan.archived_at
  )
}

const isOneTimeSubscriptionPlan = (plan: any): boolean => {
  return plan && plan.interval === SUBSCRIPTION_PLAN_INTERVALS.ONE_TIME;
}

const userHasCardOnFile = (group: any): boolean => {
  const userMembership = group && group.user_membership
  if (!userMembership) return false

  return !!(
    (
      userMembership &&
      userMembership.pm_type &&
      userMembership.pm_last_four
    ) 
      ||
    (
      userMembership &&
      userMembership.user &&
      userMembership.user.pm_type &&
      userMembership.user.pm_last_four
    )
    )
}

const isSameDayEvent = (event: any) => {
  const userTimezone = moment.tz.guess();
  const userTimezoneOffset = new Date().getTimezoneOffset();

  const startDate = moment(getDateObjectFromGivenTimezone(null, userTimezone, event.end_time)).subtract(userTimezoneOffset / 60, "hours");
  const endDate = moment(getDateObjectFromGivenTimezone(null, userTimezone, event.start_time)).subtract(userTimezoneOffset / 60, "hours");

  return startDate.isSame(endDate, "day");
}

// const newMemberCheck = (isAuthed: any, group: any) => {
//   const onboardingDateAfterTheDeployement = moment('Mon Mar 18 2024 13:00:00', 'ddd MMM DD YYYY HH:mm:ss');
//   return Boolean(
//     isAuthed &&
//     group &&
//     group.user_membership &&
//     (
//       group.user_membership.approved_on === null ||
//       (
//         group.user_membership.approved_on &&
//         moment.utc(group.user_membership.approved_on).isAfter(moment.utc(onboardingDateAfterTheDeployement)) &&
//         moment.utc(group.user_membership.approved_on).isSameOrAfter(moment.utc().subtract(14, 'days'))
//       )
//     )
//   );

// A member is "new" for a 30-minute window around their approval. We use a
// window (rather than a strict approved_on >= last_seen comparison) because the
// membership-data endpoint bumps last_seen to the *current* visit, so a member
// approved mid-session ends up with last_seen a few seconds/minutes after
// approved_on. The window absorbs that and keeps them "new" for their first
// post-approval visit.
//
// `lastSeen` is the current user's last_seen timestamp, fetched from the
// membership-data endpoint and threaded in via currentUserLastSeenAtom:
//   - undefined => not loaded yet. Treat as new if approved within the window.
//   - null      => never seen => new.
//   - timestamp => new if last_seen is within the window after approved_on.
const NEW_MEMBER_WINDOW_MINUTES = 30;

const newMemberCheck = (isAuthed: any, group: any, lastSeen?: any) => {
  if (!(isAuthed && group && group.user_membership)) {
    return false;
  }

  // An owner or admin is never a new member. Right after creating a community the
  // creator's membership is freshly approved and never seen, which read as "new" and
  // redirected them to new_members_default_page.
  if (isAdmin(group.user_membership)) {
    return false;
  }

  const approvedOn = group.user_membership.approved_on;

  // Membership not yet approved -> treat as new (unchanged behaviour).
  if (approvedOn === null || approvedOn === undefined) {
    return true;
  }

  // last_seen not loaded yet -> new if approved within the window.
  if (lastSeen === undefined) {
    return moment.utc(approvedOn).isSameOrAfter(moment.utc().subtract(NEW_MEMBER_WINDOW_MINUTES, 'minutes'));
  }

  // Never seen -> new.
  if (lastSeen === null) {
    return true;
  }

  // Seen within the window after approval still counts as new.
  return moment.utc(lastSeen).isSameOrBefore(moment.utc(approvedOn).add(NEW_MEMBER_WINDOW_MINUTES, 'minutes'));
}

const onboardingPopUpShouldOpen = (group: any, isAuthed: boolean, requiredDefaultKeys: any, requiredCustomFields: any) => {
  // determine if onboarding pop up should open
  // This should be case if the case the list length is greater than 0

  // Don't surface onboarding to members whose subscription plan is canceled or past due
  if (group && (isSubscriptionPaymentCanceled(group) || isSubscriptionPaymentFailedOrPastDue(group))) {
    return []
  }

  const customFieldsAreSet = Boolean(
    isAuthed &&
    group &&
    group.onboarding_steps &&
    group.onboarding_steps.custom_fields
  )

  const defaultFieldsAreSet = Boolean(
    isAuthed &&
    group &&
    group.onboarding_steps &&
    group.onboarding_steps.default_fields
  )

  const welcomeScreenIsSet = Boolean(
    isAuthed &&
    group &&
    group.user_membership &&
    group.user_membership.approved_on &&
    group.onboarding_steps &&
    group.onboarding_steps.welcome
  )

  const IntroductionScreenIsSet = Boolean(
    isAuthed &&
    group &&
    group.user_membership &&
    group.user_membership.approved_on &&
    group.onboarding_steps &&
    group.onboarding_steps.introduction
  )

  const customFieldsStepIsCompleted = Boolean(
    isAuthed &&
    group &&
    group.user_membership &&
    group.user_membership.onboarding_completed &&
    group.user_membership.onboarding_completed.custom_fields
  )

  const defaultFieldsStepIsCompleted = Boolean(
    isAuthed &&
    group &&
    group.user_membership &&
    group.user_membership.onboarding_completed &&
    group.user_membership.onboarding_completed.default_fields
  )

  const welcomeScreenStepIsCompleted = Boolean(
    isAuthed &&
    group &&
    group.user_membership &&
    group.user_membership.onboarding_completed &&
    group.user_membership.onboarding_completed.welcome
  )

  const IntroductionScreenStepIsCompleted = Boolean(
    isAuthed &&
    group &&
    group.user_membership &&
    group.user_membership.onboarding_completed &&
    group.user_membership.onboarding_completed.introduction
  )

  const welcomeScreenIsSetForBoth = Boolean(
    isAuthed &&
    group &&
    group.user_membership &&
    group.user_membership.approved_on &&
    !isTeamMember(group.user_membership) &&
    group.onboarding_steps &&
    group.onboarding_steps.required_welcome
  )

  const defaultFieldsAreSetForBoth = Boolean(
    isAuthed &&
    group &&
    !isTeamMember(group.user_membership) &&
    group.onboarding_steps &&
    group.onboarding_steps.required_default_fields
  )


  const customFieldsAreSetForBoth = Boolean(
    isAuthed &&
    group &&
    !isTeamMember(group.user_membership) &&
    group.onboarding_steps &&
    group.onboarding_steps.required_custom_fields
  )

  const IntroductionScreenIsSetForBoth = Boolean(
    isAuthed &&
    group &&
    group.user_membership &&
    group.user_membership.approved_on &&
    !isTeamMember(group.user_membership) &&
    group.onboarding_steps &&
    group.onboarding_steps.required_introduction
  )

  const onboardingStepsList = []

  if (newMemberCheck(isAuthed, group)) {
    if (welcomeScreenIsSet && !welcomeScreenStepIsCompleted) {
      onboardingStepsList.push('welcome')
    }
    if (defaultFieldsAreSet && !defaultFieldsStepIsCompleted && requiredDefaultKeys.length > 0
    ) {
      onboardingStepsList.push('default_fields')
    }
    if (customFieldsAreSet && !customFieldsStepIsCompleted) {
      onboardingStepsList.push('custom_fields')
    }
    if (IntroductionScreenIsSet && !IntroductionScreenStepIsCompleted) {
      onboardingStepsList.push('introduction')
    }
  }
  else {
    if (welcomeScreenIsSet && !welcomeScreenStepIsCompleted && welcomeScreenIsSetForBoth) {
      onboardingStepsList.push('welcome')
    }
    if (defaultFieldsAreSet && requiredDefaultKeys.length > 0 && defaultFieldsAreSetForBoth) {
      onboardingStepsList.push('default_fields')
    }
    if (
      customFieldsAreSet &&
      // !customFieldsStepIsCompleted &&
      requiredCustomFields && requiredCustomFields.length > 0 &&
      customFieldsAreSetForBoth) {
      onboardingStepsList.push('custom_fields')
    }
    if (IntroductionScreenIsSet && !IntroductionScreenStepIsCompleted && IntroductionScreenIsSetForBoth) {
      onboardingStepsList.push('introduction')
    }
  }
  return onboardingStepsList

}

const slugify = (string: string) => {
  return string
    .toLowerCase() // Convert the string to lowercase
    .replace(/[^\w\s-]/g, '') // Remove non-word characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with a single hyphen
    .trim(); // Trim leading and trailing spaces
}

const validateSpecialChars = (imageFile: File) => {
  const specialChars = /[#%]/g;
  if (specialChars.test(imageFile.name)) {
    toast(`Please remove any special characters like # or % from the file name: ${imageFile.name}`, {
      type: 'error',
      autoClose: 5000,
      position: "top-center",
      hideProgressBar: true,
      theme: "light"
    });
    return false;
  }
  return true
}

const convertHeicToJpeg = async (file: any) => {
  try {
    if (typeof window === "undefined" || !heic2any) {
      throw new Error("heic2any is not available in this environment");
    }

    const convertedBlob = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.8,
    });
    return convertedBlob;
  } catch (error) {
    console.error('Error converting HEIC to JPEG:', error);
    return null;
  }
};

function getSubdomainIndex(hostname: string): number {
  const parts = hostname.split('.').reverse();
  if (parts.length === 1) return 0; // 'com'
  if (parts.length === 2) return 1; // 'example.com'
  return 2; // 'www.example.com' or more
}

function isActiveCommunityMember(userMembership: any): boolean {
  return (
    isTeamMember(userMembership) ||
    (
      userMembership &&
      userMembership.active_subscription &&
      (
        userMembership.active_subscription.stripe_status === "active" ||
        userMembership.active_subscription.stripe_status === "trialing" ||
        userMembership.active_subscription.stripe_status === "on_grace"
      ) &&
      !isMembershipPendingApproval(userMembership)
    )
  )
}

function isChannelManaged(userMembership: any, channelData: any): boolean {
  let channelIsManaged: boolean = false;

  if (
    userMembership &&
    userMembership.managed_channels &&
    channelData
  ) {
    userMembership.managed_channels.forEach((channel: any) => {
      if (channel.id === channelData.id) {
        channelIsManaged = true;
      }
    })
  }

  return channelIsManaged;
}

function isChannelMember(userMembership: any, channelData: any): boolean {
  return (
    isAdmin(userMembership) ||
    (
      isModerator(userMembership) &&
      isChannelManaged(userMembership, channelData)
    ) ||
    (
      channelData &&
      channelData.membership &&
      !channelData.membership.deleted_at
    )
  )
}

function isMembershipPendingApproval(userMembership: any): boolean {
  return (
    userMembership &&
    !userMembership.approved_on
  )
}

function isMembershipHasIncompleteSubscriptionData(userMembership: any): boolean {
  return !!(
    userMembership &&
    userMembership.pending_data != null &&
    userMembership.pending_data !== 'null' &&
    typeof userMembership.pending_data === 'object' &&
    userMembership.pending_data.incomplete_subscription_data
  )
}

// Utility function to format date to 'YYYY-MM-DD HH:MM:SS'
const getDateTimeString = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

// Function to add days to today's date
const addDaysToDate = (newDate: Date, days: number) => {
  const date = new Date(newDate);
  date.setDate(date.getDate() + days);
  return getDateTimeString(date);
};

const MaxPhotoFileSize: number = 5000000;

const validatePhotoFileSize = (file: any): boolean => {
  if (file && file.size > MaxPhotoFileSize) {
    toast("Maximum image upload size is 5MB.", {
      type: 'error',
      autoClose: 3000,
      position: "top-center",
      hideProgressBar: true,
      theme: "light"
    });
    return false;
  }
  return true;
}

const toastPhotoUploadFailed = () => {
  toast("The image could not be uploaded. Please try again.", {
    type: 'error',
    autoClose: 3000,
    position: "top-center",
    hideProgressBar: true,
    theme: "light"
  });
}

const getBunnyVideoLibraryId = (videoURL: string) => {
  return videoURL.split('/')[videoURL.split('/').length - 2];
}

const getBunnyMP4VideoUrl = (bunnyLibraryType: BunnyLibraryTypes | 'posts', videoUrl: string, resolution?: string) => {

  const getVideoId = () => {
    return videoUrl.split("/")[5];
  }

  switch (bunnyLibraryType) {
    case BunnyLibraryTypes.COURSE_INTRO:
      return `https://${appConfig.bunny.course_intro_videos.pull_zone}.b-cdn.net/${getVideoId()}/play_${resolution ? resolution : '720'}p.mp4`;
    case BunnyLibraryTypes.COURSE_CONTENT:
      return `https://${appConfig.bunny.course_content_videos.pull_zone}.b-cdn.net/${getVideoId()}/play_${resolution ? resolution : '720'}p.mp4`;
    case BunnyLibraryTypes.STANDALONE_LIBRARY_CONTENT:
      return `https://${appConfig.bunny.standalone_library_videos.pull_zone}.b-cdn.net/${getVideoId()}/play_${resolution ? resolution : '720'}p.mp4`
    default:
      return `https://${appConfig.bunny.post_videos.pull_zone}.b-cdn.net/${getVideoId()}/play_${resolution ? resolution : '720'}p.mp4`;
  }
}

export interface BunnyCaptionTrack {
  kind: 'subtitles';
  src: string;
  srcLang: string;
  label: string;
  default: boolean;
}

// Builds the public Bunny iframe embed url from a content_url
// (https://video.bunnycdn.com/play/{libraryId}/{videoId}). The embed page
// server-renders one <track> per caption the video has, so it's the source of
// truth for the available caption list - and it's CORS-open with no API key.
const getBunnyEmbedUrl = (contentUrl: string) => {
  const parts = contentUrl.split('/');
  const videoId = parts[5];
  const libraryId = parts[parts.length - 2];
  return `https://iframe.mediadelivery.net/embed/${libraryId}/${videoId}`;
}

// Returns a human-friendly label for a language code, falling back to Bunny's
// own label (when it's not the generic "Captions") and finally the raw code.
const getCaptionLabel = (srcLang: string, bunnyLabel: string) => {
  try {
    // Intl.DisplayNames isn't in the project's TS lib target, but it exists at
    // runtime in all supported browsers.
    const displayNames = new (Intl as any).DisplayNames(
      [(typeof navigator !== 'undefined' && navigator.language) || 'en'],
      { type: 'language' }
    );
    const name = displayNames.of(srcLang);
    if (name && name.toLowerCase() !== srcLang.toLowerCase()) {
      return name;
    }
  } catch (e) {
    // Intl.DisplayNames unsupported - fall through to the label/code below.
  }
  if (bunnyLabel && bunnyLabel.trim().toLowerCase() !== 'captions') {
    return bunnyLabel;
  }
  return srcLang.toUpperCase();
}

// Parses the <track kind='captions' ...> tags out of a Bunny embed page into
// a ReactPlayer-ready tracks array. The first track is marked default.
const parseBunnyCaptionTracks = (embedHtml: string): BunnyCaptionTrack[] => {
  const trackTags = embedHtml.match(/<track[^>]*>/gi) || [];
  const tracks: BunnyCaptionTrack[] = [];

  trackTags.forEach((tag) => {
    if (!/kind\s*=\s*['"]captions['"]/i.test(tag)) {
      return;
    }
    const srcMatch = tag.match(/\bsrc\s*=\s*['"]([^'"]+)['"]/i);
    const langMatch = tag.match(/\bsrclang\s*=\s*['"]([^'"]+)['"]/i);
    const labelMatch = tag.match(/\blabel\s*=\s*['"]([^'"]*)['"]/i);
    if (!srcMatch || !langMatch) {
      return;
    }
    const srcLang = langMatch[1];
    tracks.push({
      kind: 'subtitles',
      src: srcMatch[1],
      srcLang,
      label: getCaptionLabel(srcLang, labelMatch ? labelMatch[1] : ''),
      default: tracks.length === 0,
    });
  });

  return tracks;
}

const encodeURLLastPathSegment = (url: string) => {
  if (!url) {
    return '';
  }
  const urlParts = url.split('/');

  const lastPathSegment = urlParts.pop();

  const remainingURLString = urlParts.join('/');

  return `${remainingURLString}/${encodeURIComponent(lastPathSegment ? lastPathSegment : '')}`;
}

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'auto',
  });
}

function isEmptyText(htmlString: any) {
  // Check for image tags
  if (/<img/.test(htmlString)) {
    return false; // Contains an image, so not empty
  }

  // Convert the HTML string to ContentState
  const blocksFromHTML = convertFromHTML(htmlString);
  const contentState = ContentState.createFromBlockArray(
    blocksFromHTML.contentBlocks,
    blocksFromHTML.entityMap
  );

  // Check if the content is empty
  return contentState.getPlainText().trim() === '';
}

function postDescCount(htmlString: any) {
  // Remove image tags from the HTML string
  const htmlWithoutImages = htmlString.replace(/<img[^>]*>/g, "");

  // Convert the HTML string to ContentState
  const blocksFromHTML = convertFromHTML(htmlWithoutImages);
  const contentState = ContentState.createFromBlockArray(
    blocksFromHTML.contentBlocks,
    blocksFromHTML.entityMap
  );

  // Get the plain text and return its length
  const plainText = contentState.getPlainText().trim();
  return plainText.length; // Return the length of the plain text
}


function truncateString(str: string, num: number) {
  // Check if the length of the string is greater than the given number
  if (str.length > num) {
    // If it is, truncate the string and add '...' at the end
    return str.slice(0, num) + '...';
  } else {
    // If it's not, return the string as is
    return str;
  }
}

const replaceNbspWithSpace = (inputString: string) => {
  var replacedString = inputString.replace(/&nbsp;/g, ' ');

  return replacedString;
}

const replaceAmpersandWithCharacter = (inputString: string) => {
  var replacedString = inputString.replace(/&amp;/g, '&');

  return replacedString;
}

const groupStorageAddonIsInactive = (group: any) => {
  return(
    group &&
    group.storage_addon_status &&
    (
      group.storage_addon_status === 'canceled' ||
      group.storage_addon_status === 'past_due'
    )
  )
}

// Free communities unlock public content by verifying with Stripe rather than
// by upgrading.
const planBlocksPublicVisibility = (group: any) => {
  if (isGroupOnFreePlan(group)) {
    return Boolean(group && group.effective_public_visibility === false);
  }

  const plan = getGroupPlan(group);
  return Boolean(
    plan &&
    plan.content_visibility &&
    plan.content_visibility.public === false
  );
}

const planBlocksVisibility = (group: any, visibility: any) => {
  return visibility === 'public' && planBlocksPublicVisibility(group);
}

// Which prompt a blocked community gets: a free one can verify with Stripe,
// anyone else has to upgrade.
const publicVisibilityPrompt = (group: any): 'verify' | 'upgrade' | null => {
  if (!planBlocksPublicVisibility(group)) {
    return null;
  }

  return isGroupOnFreePlan(group) ? 'verify' : 'upgrade';
}

const planBlocksDirectVideoUpload = (group: any) => {
  return Boolean(group && group.effective_direct_video_upload === false);
}

// Reads a dot-path boolean entitlement, e.g. 'learning_tools.assignments'. Only an
// explicit false blocks, so plans that predate the flag are untouched.
const planBlocksFeature = (group: any, feature: string) => {
  const plan = getGroupPlan(group);
  return Boolean(plan && getNestedValue(plan, feature) === false);
}

// Per-file ceiling in MB. null means the payload carried no limit; 0 is a real
// limit that blocks every upload, so both consumers test against null.
const getUploadLimit = (group: any) => {
  if (
    !group ||
    group.effective_upload_limit === null ||
    group.effective_upload_limit === undefined
  ) {
    return null;
  }

  return group.effective_upload_limit;
}

const getUploadLimitLabel = (group: any) => {
  const limit = getUploadLimit(group);

  if (limit === null) {
    return "";
  }

  return limit > 999 ? mBToGb(limit, 0) : `${limit}MB`;
}

const exceedsPlanUploadLimit = (group: any, file: any) => {
  const limit = getUploadLimit(group);

  if (limit === null || !file || !file.size) {
    return false;
  }

  return (file.size / BYTES_IN_MB) > limit;
}

const planBlocksSegmentVisibility = (group: any) => {
  const plan = getGroupPlan(group);

  if (!plan) {
    return false;
  }

  if (plan.content_visibility && plan.content_visibility.by_segment === false) {
    return true;
  }

  return !plan.segments;
}

const fileInputAcceptsVideo = (accept: any) => {
  if (!accept) {
    return false;
  }

  const normalisedAccept = accept.toLowerCase();
  return ['video', 'mp4', 'mov', 'avi', 'flv', 'webm'].some(
    (videoType: string) => normalisedAccept.indexOf(videoType) !== -1
  );
}

const groupStorageLimitIsMaxedOut = (group: any) => {  
  if(groupStorageAddonIsInactive(group)) {
    return true;
  }

  if (
    group &&
    group.max_str
  ) {
    return (
      group &&
      group.str_usg &&
      group.str_usg >= group.max_str
    )
  }

  return (
    group &&
    group.str_usg &&
    group.active_subscription &&
    group.active_subscription.plan &&
    group.active_subscription.plan.storage_limit &&
    group.str_usg >= group.active_subscription.plan.storage_limit
  )
}

const groupStorageUsageIsEightyPercent = (group: any) => {
  if (
    group &&
    group.max_str
  ) {
    if (
      group &&
      group.str_usg &&
      group.str_usg >= (group.max_str * 0.8)
    ) {
      return Math.round((group.str_usg / group.max_str) * 100);
    }
    return null;
  }

  if (
    group &&
    group.str_usg &&
    group.active_subscription &&
    group.active_subscription.plan &&
    group.active_subscription.plan.storage_limit &&
    group.str_usg >= (group.active_subscription.plan.storage_limit * 0.8)
  ) {
    return Math.round((group.str_usg / group.active_subscription.plan.storage_limit) * 100);
  }
  return null;
}

const groupStorageLimitWillMaxOut = (group: any, size: number) => {
  if(
    group &&
    group.str_usg &&
    size
  ) {
    const nextStorageUsage = group.str_usg + size;
    return groupStorageLimitIsMaxedOut({
      ...group,
      str_usg: nextStorageUsage,
    });
  }

  return false;
}

function isWithinPastXDays(date: string, days: number) {
  if (!date) {
    return false;
  }

  const currentDate = dayjs().utc();
  const targetDate = dayjs.utc(date);

  // Check if the date is within the last X days
  return targetDate.isBetween(currentDate.subtract(days, 'day'), currentDate, null, '[]');
}

function getExpirationDate(date: string, days: number) {
  const createdAtDate = dayjs.utc(date); // Parse created_at in UTC
  return createdAtDate.add(days, 'day'); // Add 7 days to created_at

}

// Days left in the new-community offer window, rounded up, at least 1.
function getNewUserOfferDaysLeft(group: any) {
  if (!group || !group.created_at) return 1;
  return Math.max(Math.ceil(getExpirationDate(group.created_at, 7).diff(new Date(), 'day', true)), 1);
}

// Date (e.g. "October 29") a new community can upgrade by for the offer, or '' when off or expired.
function getNewUserUpgradeBefore(group: any) {
  const goLiveDate = process.env.REACT_APP_FREE_PLAN_UPGRADE_OFFER_GO_LIVE_DATE;
  if (!goLiveDate || !group || !group.created_at) return '';

  if (dayjs.utc(group.created_at).isBefore(dayjs.utc(goLiveDate))) return '';

  const deadline = getExpirationDate(group.created_at, 7);
  if (dayjs().utc().isAfter(deadline)) return '';

  return deadline.local().format('MMMM D');
}

// True when the code is the new-community upgrade coupon from env.
function isNewCommunityCoupon(code: any) {
  return Boolean(code && code === process.env.REACT_APP_FREE_PLAN_UPGRADE_OFFER_COUPON);
}

const NEW_COMMUNITY_COUPON_KEY = 'apply_new_community_coupon';

// Storage can be blocked, so a failed read counts as not set.
function newCommunityCouponClicked() {
  try {
    return localStorage.getItem(NEW_COMMUNITY_COUPON_KEY) === 'true';
  } catch (e) {
    return false;
  }
}

function markNewCommunityCouponClicked() {
  try {
    localStorage.setItem(NEW_COMMUNITY_COUPON_KEY, 'true');
  } catch (e) {
    return;
  }
}

function clearNewCommunityCouponClicked() {
  try {
    localStorage.removeItem(NEW_COMMUNITY_COUPON_KEY);
  } catch (e) {
    return;
  }
}

function hasIntegration(group: any, slug: string) {
  let integration = false;

  if (
    group &&
    group.connected_applications &&
    group.connected_applications.length
  ) {
    group.connected_applications.forEach((application: any) => {
      if (application.slug === slug) {
        integration = true;
      }
    });
  }

  return integration;
}

function getDaysLeft(trial_ends_at: string | Date): number | string {
  const trialEndDate = typeof trial_ends_at === 'string'
    ? new Date(trial_ends_at)
    : new Date(trial_ends_at);

  if (isNaN(trialEndDate.getTime())) {
    throw new Error('Invalid date provided');
  }

  const today = new Date();

  // Set both dates to start of day (midnight) for calendar day comparison
  const trialEndDay = new Date(
    trialEndDate.getUTCFullYear(),
    trialEndDate.getUTCMonth(),
    trialEndDate.getUTCDate()
  );

  const todayDay = new Date(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate()
  );

  // Calculate the difference in milliseconds
  const diffMs = trialEndDay.getTime() - todayDay.getTime();

  // If trial has expired (trial end date is before today)
  if (diffMs < 0) {
    return 0;
  }

  // Convert milliseconds to calendar days
  const daysLeft = Math.round(diffMs / (1000 * 60 * 60 * 24));

  // If 0 days left, it means trial ends today - show hours remaining
  if (daysLeft === 0) {
    const now = new Date();
    const hoursLeft = Math.max(0, Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60)));

    if (hoursLeft <= 0) {
      return 0;
    }

    return `${hoursLeft} hour${hoursLeft !== 1 ? 's' : ''}`;
  }

  return `${daysLeft} day${daysLeft !== 1 ? 's' : ''}`;
}

function getTrialInfo(trial_ends_at: any) {

  const userTimezone = dayjs.tz.guess();

  const expirationDateUTC = dayjs.utc(trial_ends_at);

  const upgradeBeforeDate = expirationDateUTC
    .tz(userTimezone)
    .format('MMM D, [at] hh:mm A');

  return upgradeBeforeDate

}


// Check if banner should be shown
function shouldDownloadShowSmartBanner(): boolean {
  const lastDismissedTime = localStorage.getItem("appBannerDismissedTimestamp");
  const twoDaysInMs = 2 * 24 * 60 * 60 * 1000;
  const now = Date.now();

  return !lastDismissedTime || (now - Number(lastDismissedTime)) >= twoDaysInMs;
}


function validateURL(input: string) {
  try {
    const urlPattern = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$/;
    const url = new URL(input.startsWith('http') ? input : `https://${input}`);

    if (input.length > 255) {
      toast("The URL is too long. Try using a shorter or more direct link.", {
        type: 'error',
        autoClose: 4000,
        position: "top-center",
        hideProgressBar: true,
        theme: "light"
      });
      return false;
    }

    // Check if the input starts with 'http://' or 'https://'
    if (!input.startsWith('http://') && !input.startsWith('https://')) {
      toast("Invalid URL. Add 'http://' or 'https://' at the beginning of the URL (https://example.com)", {
        type: 'error',
        autoClose: 4000,
        position: "top-center",
        hideProgressBar: true,
        theme: "light"
      });
      return false;
    }

    if (!['http:', 'https:'].includes(url.protocol)) {
      toast("Invalid URL. Add 'http://' or 'https://' at the beginning of the URL (https://example.com)", {
        type: 'error',
        autoClose: 4000,
        position: "top-center",
        hideProgressBar: true,
        theme: "light"
      });
      return false
    }

    if (!url.hostname.includes('.')) {
      toast("Invalid URL. The URL should include a '.' (https://example.com)", {
        type: 'error',
        autoClose: 4000,
        position: "top-center",
        hideProgressBar: true,
        theme: "light"
      });
      return false
    }

    if (urlPattern.test(input) === false) {
      toast('Please enter a valid URL (https://example.com)', {
        type: 'error',
        autoClose: 4000,
        position: "top-center",
        hideProgressBar: true,
        theme: "light"
      });
      return false
    }

    return true
  } catch {
    toast('Please enter a valid URL (https://example.com)', {
      type: 'error',
      autoClose: 4000,
      position: "top-center",
      hideProgressBar: true,
      theme: "light"
    });
    return false
  }
}

function validateURLReturnError(input: string) {
  if(!input) {
      return {
        isValid: false,
        error: "URL cannot be empty",
      };
  }
  try {
    const urlPattern = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$/;
    const url = new URL(input.startsWith('http') ? input : `https://${input}`);

    if (input.length > 255) {
      return {
        isValid: false,
        error: "The URL is too long. Try using a shorter or more direct link.",
      };
    }

    // Check if the input starts with 'http://' or 'https://'
    if (!input.startsWith('http://') && !input.startsWith('https://')) {
      return {
        isValid: false,
        error: "Invalid URL. Add 'http://' or 'https://' at the beginning of the URL (https://example.com)",
      };
    }

    if (!['http:', 'https:'].includes(url.protocol)) {
      return {
        isValid: false,
        error: "Invalid URL. Add 'http://' or 'https://' at the beginning of the URL (https://example.com)",
      }
    }

    if (!url.hostname.includes('.')) {
      return {
        isValid: false,
        error: "Invalid URL. The URL should include a '.' (https://example.com)",
      }
    }

    if (urlPattern.test(input) === false) {
      return {
        isValid: false,
        error: "Please enter a valid URL (https://example.com)",
      }
    }

    return {
      isValid: true,
      error: ""
    }
  } catch {
    return {
      isValid: false,
      error: "Please enter a valid URL (https://example.com)",
    }
  }
}

const moderatorHasAdditionalPerms = (group: any, perm: string) => {
  return (
    group &&
    group.user_membership &&
    group.user_membership.permissions &&
    group.user_membership.permissions.length > 0 &&
    group.user_membership.permissions.some((p: any) =>
      p.slug === perm
    )
  );
};

const moderatorIsAnAuthorizedForChannels = (group: any) => {
  return (
    group &&
    group.user_membership &&
    group.user_membership.managed_channels &&
    group.user_membership.managed_channels.length > 0
  );
};

const moderatorIsAnAuthorizedForCourses = (group: any) => {
  return (
    group &&
    group.user_membership &&
    group.user_membership.managed_courses &&
    group.user_membership.managed_courses.length > 0
  );
}

const moderatorIsAuthorizedForCourse = (userMembership: any, course: any) => {
  if(
    !(
      userMembership &&
      userMembership.managed_courses &&
      userMembership.managed_courses.length
    ) ||
    !course
  ) {
    return false;
  }

  let isAuthorizedForCourse = false;

  userMembership.managed_courses.forEach((managedCourse: any) => {
    if(managedCourse.id === course.id) {
      isAuthorizedForCourse = true;
    }
  });

  return isAuthorizedForCourse;
}

const getTopOffsetForMobile = (bannerEl: string) => {
  // Check for environment support
  if (typeof window === 'undefined') {
    return 0;
  }

  // Get safe area inset top value
  const safeAreaInsetTop = parseInt(
    getComputedStyle(document.documentElement).getPropertyValue('--sat') || '0',
    10
  );

  // Get the actual banner height if it exists
  const bannerElement = document.getElementById(bannerEl);
  const bannerHeight = bannerElement ? bannerElement.offsetHeight : 0;

  // Get computed styles to check for margins/padding
  if (bannerElement) {
    const bannerStyles = window.getComputedStyle(bannerElement);
    const bannerMarginBottom = parseInt(bannerStyles.marginBottom || '0', 10);

    // Return total offset including the banner height, its margin, and safe area
    return safeAreaInsetTop + bannerHeight + bannerMarginBottom;
  }

  return safeAreaInsetTop;
};

const determineRolelabel = (member: any) => {
  if (member.role_label) {
    return member.role_label.label.name
  } else if (member.role) {
    if (member.role) {
      if (member.role.id === RoleIds.Owner) {
        return "Owner"
      }

      if (member.role.id === RoleIds.Admin && !member.communitySupport) {
        return "Admin"
      }

      if (member.role.id === RoleIds.Moderator) {
        return "Moderator"
      }

      if (member.role.id === RoleIds.Member) {
        return "Member"
      }

      if (member.role.id === RoleIds.Admin && member.communitySupport) {
        return "Support"
      }

      return "Member";
    }
    else if (member.user_role_label) {
      return member.user_role_label
    }
  }
};

// True only while the community's Stripe subscription status is "trialing".
const isGroupTrialing = (group: any) => {
  return !!(
    group &&
    group.active_subscription &&
    group.active_subscription.stripe_status === "trialing"
  );
}

const isCommunityOnMonthlyTrialExcludingBusinessPlan = (group: any) => {
  return (
    group &&
    group.active_subscription &&
    group.active_subscription.on_trial &&
    (
      (group.active_subscription.plan.interval === BillingCycles.MONTHLY) &&
      (group.active_subscription.plan.name !== PlanNames.BUSINESS)
    )
  )
}

const getDefaultPageKey = (isAuthed: boolean, group: any, lastSeen?: any) => {
  if (!isAuthed) {
    return 'logged_out_default_page';
  } else {
    if (
      group &&
      group.user_membership &&
      isActiveCommunityMember(group.user_membership)
    ) {
      // A newly approved member who hasn't been active since approval lands on
      // the dedicated new-members page instead of the regular members page.
      if (newMemberCheck(isAuthed, group, lastSeen)) {
        return 'new_members_default_page'
      }

      return 'logged_in_members_default_page'
    }

    return 'logged_in_non_members_default_page';
  }
}

const getDefaultCommunityPage = (communityDefaultPages: any, defaultPageKey: string, group: any) => {
  if (!group) {
    return '/about-community';
  }

  if(
    defaultPageKey === 'logged_out_default_page' &&
    communityDefaultPages &&
    communityDefaultPages[`${defaultPageKey}_type`] === 'signin'
  ) {
    return '/signin';
  }

  if (
    (
      defaultPageKey === 'logged_out_default_page' ||
      defaultPageKey === 'logged_in_non_members_default_page'
    ) &&
    (
      !communityDefaultPages ||
      !communityDefaultPages[defaultPageKey]
    )
  ) {
    return '/about-community';
  }

  if (!communityDefaultPages || !communityDefaultPages[defaultPageKey]) {
    if (
      group &&
      group.home_page_config &&
      group.home_page_config.status
    ) {
      return `/home`;
    }

    return `/channel/${group.default_channel.slug}`;
  }

  switch (communityDefaultPages[`${defaultPageKey}_type`]) {
    case 'channel':
      return `/channel/${communityDefaultPages[defaultPageKey]}`;
    case 'menu_tab':
      if (communityDefaultPages[defaultPageKey] === 'people') {
        return `/members`
      }

      return `/${communityDefaultPages[defaultPageKey]}`;
    case 'redirect':
      return `${communityDefaultPages[defaultPageKey]}`;
    case 'landing_page':
      return `/`;
    default:
      return `/about-community`;
  }
}
const canUserPostInChannel = (userMembership: any, channel: any) => {
  return (
    isTeamMember(userMembership) ||
    (
      (
        channel.membership &&
        !channel.membership.deleted_at
      ) &&
      channel.posting_permission === "members" &&
      !channelAccessRestricted(channel, { user_membership: userMembership })
    )
  )
}


const ensureHttps = (link: string) => {
  if (typeof link !== 'string') return '';

  if (link === '#') {
    return link;
  }

  // Trim leading/trailing whitespace
  const trimmedLink = link.trim();

  if (trimmedLink.startsWith('https://')) {
    return trimmedLink;
  } else {
    return `https://${trimmedLink.replace(/^https?:\/\//, '')}`;
  }
}

const removeProtocolFromURL = (url: string) => {
  return url.replace(/^https?:\/\//, '');
}

const getPageNumber = () => {
  if (window !== undefined) {
    const searchParams = new URLSearchParams(window.location.search);
    const pageNumber = Number(searchParams.get('page'));
    return pageNumber;
  }
}

export const getYouTubeVideoId = (url: string): string => {
  try {
    const parsedUrl: any = new URL(url);
    const hostname = parsedUrl.hostname;
    const pathname = parsedUrl.pathname;

    // Case 1: youtube.com/watch?v=VIDEO_ID
    if (hostname.includes("youtube.com") && parsedUrl.searchParams.has("v")) {
      return parsedUrl.searchParams.get("v");
    }

    // Case 2: youtu.be/VIDEO_ID
    if (hostname === "youtu.be") {
      return pathname.split("/")[1];
    }

    // Case 3: youtube.com/embed/VIDEO_ID or /live/VIDEO_ID or /shorts/VIDEO_ID
    const match = pathname.match(/^\/(embed|live|shorts)\/([^/?]+)/);
    if (match) {
      return match[2];
    }

    return '';
  } catch (e) {
    return '';
  }
};

export const getStripePromise = (stripeAccountID?: any) => {
  const stripePromise = loadStripe(appConfig.stripe.public_key,
    stripeAccountID ? { stripeAccount: stripeAccountID } : undefined
  );
  return stripePromise;
};

function buildQueryString(params: Record<string, any>): string {
  const queryParts: string[] = [];

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      queryParts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  });

  return queryParts.length > 0 ? `?${queryParts.join("&")}` : "";
}

function formatStorage(usedMB: number, limitMB: number) {
  let unit = "MB";
  let divisor = 1;

  if (usedMB >= 1000000) {
    unit = "TB";
    divisor = 1000000; // 1 TB = 1,000,000 MB
  } else if (usedMB >= 1000) {
    unit = "GB";
    divisor = 1000; // 1 GB = 1,000 MB
  }

  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  const used = formatter.format(usedMB / divisor);
  const limit = formatter.format(limitMB / divisor);

  return `${used} / ${limit} ${unit}`;
}

const openGleap = (group: any) => {
  if (group) {
    Gleap.initialize(appConfig.gleap.api_key as string);
    Gleap.identify(`${group.user_membership.user.id}`, {
      name: group.user_membership.user.full_name,
      email: group.user_membership.user.email,
    });
    Gleap.open();
  }
};

// Lands the member straight in a new support conversation rather than the widget home.
const openGleapConversation = (group: any) => {
  if (!group) {
    return;
  }
  Gleap.initialize(appConfig.gleap.api_key as string);
  Gleap.identify(`${group.user_membership.user.id}`, {
    name: group.user_membership.user.full_name,
    email: group.user_membership.user.email,
  });
  if (typeof (Gleap as any).startConversation === 'function') {
    Gleap.startConversation();
  } else {
    Gleap.open();
  }
};

const userHasPermissionToLibraryContent = (group: any, libraryContent: any) => {
  const nonFreeactivePlans = group && group.plans && group.plans.filter((plan: any) => (
    plan.archived_at === null && !plan.hidden && plan.price > 0
  ));

  return (
    (
      isAdmin(group && group.user_membership) ||
      (
        isModerator(group && group.user_membership) &&
        moderatorHasAdditionalPerms(
          group,
          ModeratorPermissions.Library
        )
      )
     ) ||
    libraryContent.active_access ||
    // libraryContent.access === LibraryAccess.FREE ||
    (
      libraryContent.access === LibraryAccess.PLAN &&
      userIsOnResourcePlan(group && group.user_membership, libraryContent.authorized_plans)
    ) ||
    (
      libraryContent.access === LibraryAccess.SEGMENT &&
      userIsOnResourseSegment(group && group.user_membership, libraryContent.authorized_segments)
    ) ||
    (
      libraryContent.access === LibraryAccess.PAID &&
      nonFreeactivePlans &&
      nonFreeactivePlans.length > 0 &&
      (
        group &&
        group.user_membership &&
        group.user_membership.active_subscription &&
        group.user_membership.active_subscription.plan &&
        group.user_membership.active_subscription.plan.price
      )
    )
  );
}

function getPathFromUrl(url: string) {
  try {
    const { pathname, search, hash } = new URL(url);
    return `${pathname}${search}${hash}`;
  } catch (error) {
    console.error("Invalid URL:", (error as any).message);
    return null;
  }
}

/**
 * Checks if a user can downgrade their extra purchased storage.
 * Takes into account the base storage limit from the plan, current usage, and proposed add-on storage.
 * 
 * Example:
 * - currentUsageMB: 274590 MB
 * - baseStorageLimitMB: 200000 MB (from plan)
 * - currentAddOnStorageMB: 300000 MB (extra bought)
 * - proposedAddOnStorageMB: 100000 MB (user wants to reduce to this)
 * 
 * New total would be: 200000 + 100000 = 300000 MB
 * Since usage (274590) <= new total (300000), downgrade is allowed.
 * 
 * @param currentUsageMB - The current media storage used by the user (in MB)
 * @param baseStorageLimitMB - The base storage limit from the user's plan (in MB)
 * @param proposedAddOnStorageMB - The new add-on storage the user wants to have (in MB)
 * @returns Object containing:
 *   - canDowngrade: true if the downgrade is allowed, false otherwise
 *   - newTotalStorageMB: the new total storage after downgrade
 *   - shortfallMB: how much storage is missing if downgrade is not allowed (0 if allowed)
 *   - message: a human-readable message explaining the result
 */
export function canDowngradeExtraStorage(
  currentUsageMB: number,
  baseStorageLimitMB: number,
  proposedAddOnStorageMB: number
): {
  canDowngrade: boolean;
  newTotalStorageMB: number;
  shortfallMB: number;
  message: string;
} {
  const newTotalStorageMB = baseStorageLimitMB + proposedAddOnStorageMB;
  const canDowngrade = currentUsageMB <= newTotalStorageMB;
  const shortfallMB = canDowngrade ? 0 : currentUsageMB - newTotalStorageMB;
  
  let message = '';
  if (canDowngrade) {
    message = `Storage downgrade is allowed. New total: ${(newTotalStorageMB / 1000).toFixed(2)} GB`;
  } else {
    message = `Cannot remove storage. Current usage (${(currentUsageMB / 1000).toFixed(2)} GB) exceeds proposed limit (${(newTotalStorageMB / 1000).toFixed(2)} GB). Please free up ${(shortfallMB / 1000).toFixed(2)} GB first.`;
  }
  
  return {
    canDowngrade,
    newTotalStorageMB,
    shortfallMB,
    message
  };
}

 
/**
 * Gets the next upgrade plan based on the current subscription's plan name.
 * 
 * @param groupData - The group data object containing subscription data
 * @param plansData - The plans data object containing plan 
 * @returns The next plan name to upgrade to, or null if no upgrade is available
 * 
 */
export function getNextUpgradePlan(groupData: any, plansData: any): PlanNames | null {
  const currentPlan = groupData &&
    groupData.active_subscription &&
    groupData.active_subscription.plan;

  if (!currentPlan || !plansData || plansData.length === 0) {
    return null;
  }

  // Check if current plan is an old legacy plan with position >= 3
  const oldPlanNames = ['premium', 'business', 'enterprise'];
  if (oldPlanNames.includes(currentPlan.name.toLowerCase()) && currentPlan.position >= 3) {
    return null;
  }

  // Override for specific plans to upgrade to scale or organization
  const currentPlanName = currentPlan.name.toLowerCase();
  const toScale = ['starter', 'basic', 'basic ltd', 'appSumo tier 1', 'appSumo tier 2', 'appSumo tier 3', 'appSumo tier 4'];
  const toOrganization = ['pro ltd', 'pro plus (early bird)', 'pro plus', 'tier 4 ltd', 'pro'];

  if (toScale.map((p) => p.toLowerCase()).includes(currentPlanName)) {
    const scalePlan = plansData.find((p: any) => p.name.toLowerCase() === 'scale');
    if (scalePlan) {
      const planName = scalePlan.name;
      return planName && titleCasePlanName(planName) as PlanNames;
    }
  }

  if (toOrganization.map((p) => p.toLowerCase()).includes(currentPlanName)) {
    const orgPlan = plansData.find((p: any) => p.name.toLowerCase() === 'branded app');
    if (orgPlan) {
      const planName = orgPlan.name;
      return planName && titleCasePlanName(planName) as PlanNames;
    }
  }

  // For 'free' plan, don't use position comparison - just get the first plan in the sorted list
  if (currentPlan.name.toLowerCase() === 'free' || currentPlan.name.toLowerCase() === 'demo') {
    const sortedPlans = plansData
      .filter((plan: any) => plan.name.toLowerCase() !== 'free' && plan.name.toLowerCase() !== 'demo')
      .sort((a: any, b: any) => a.position - b.position);
    
    if (sortedPlans.length > 0) {
      const planName = sortedPlans[0].name;
      return planName && titleCasePlanName(planName) as PlanNames;
    }
    return null;
  }

  const upgradePlans = plansData
    .filter((plan: any) => plan.position > currentPlan.position && (plan.name !== currentPlan.name))
    .sort((a: any, b: any) => a.position - b.position);

  if (upgradePlans.length > 0) {
    const planName = upgradePlans[0].name;
    return planName && titleCasePlanName(planName) as PlanNames
  }

  return null;
}

export function getNextPlanToUpgradeTo(groupData: any, plansData: any, interval = 'yearly'): any {
  if (!plansData || plansData.length === 0) {
    return null;
  }

  // Use getNextUpgradePlan to get the accurate next plan name
  const nextPlanName = getNextUpgradePlan(groupData, plansData);

  
  if (!nextPlanName) {
    return null;
  }

  // Find the plan matching the name and specified interval
  const plan = plansData.find(
    (p: any) => p.name.toLowerCase() === nextPlanName.toLowerCase() && p.interval === interval
  );
  return plan || null;
}

/**
 * Helper function to get a nested value from an object using dot notation
 * Example: getNestedValue(plan, 'learning_tools.access_duration') 
 * returns plan.learning_tools.access_duration
 */
function getNestedValue(obj: any, path: string): any {
  if (!obj || !path) return undefined;
  
  const keys = path.split('.');
  let value = obj;
  
  for (const key of keys) {
    if (value === null || value === undefined) return undefined;
    value = value[key];
  }
  
  return value;
}

export function planIsAuthorized(plansData: any, feature: string, groupData: any = null, interval = 'yearly'): any {
  if (!plansData || !Array.isArray(plansData) || plansData.length === 0) {
    return null;
  }

  // Get the current plan's feature value for comparison (supports dot notation for nested features)
  const currentPlan = groupData && groupData.active_subscription && groupData.active_subscription.plan;
  const currentFeatureValue = currentPlan && getNestedValue(currentPlan, feature);

  // First, find the authorized plan using yearly plans as the source of truth
  // This ensures we get consistent plan names regardless of interval
  const sortedYearlyPlans = [...plansData]
    .filter((plan: any) => plan.interval === 'yearly')
    .sort((a: any, b: any) => a.position - b.position);

  let authorizedPlanName: string | null = null;

  // A plan "satisfies" the feature if its feature value would be an upgrade
  // over the current plan's value. Used both by the position-sorted fallback
  // scan below and by the tier-based mapping that follows.
  const planSatisfiesFeature = (plan: any): boolean => {
    const featureValue = getNestedValue(plan, feature);
    if (typeof featureValue === 'number') {
      if (typeof currentFeatureValue === 'number') {
        if (currentFeatureValue === -1) return false;
        return featureValue === -1 || featureValue > currentFeatureValue;
      }
      return featureValue > 0 || featureValue === -1;
    }
    return !!featureValue;
  };

  // Fallback: the first plan (by ascending position) that satisfies the feature.
  // Used when the current plan name isn't in the tier map below.
  for (const plan of sortedYearlyPlans) {
    if (planSatisfiesFeature(plan)) {
      authorizedPlanName = plan.name;
      break;
    }
  }

  if (!authorizedPlanName) {
    return null;
  }

  // Map the current plan name to a target tier: scale-tier plans → new Scale,
  // organization-tier (and higher) plans → new Organization. If the mapped
  // target exists in plansData with the feature enabled, use it — irrespective
  // of position. This handles the case where legacy and new plans share both
  // a name and a position (e.g. legacy Organization and new Organization both
  // at position 4), where only the new one carries branded_app.
  if (currentPlan) {
    const currentPlanNameLower = currentPlan.name.toLowerCase();
    const tierByPlanName: Record<string, string> = {
      'free': 'grow',
      'demo': 'grow',
      'starter': 'scale',
      'basic': 'scale',
      'basic ltd': 'scale',
      'appsumo tier 1': 'scale',
      'appsumo tier 2': 'scale',
      'appsumo tier 3': 'scale',
      'appsumo tier 4': 'scale',
      'grow': 'grow',
      'scale': 'scale',
      'pro': 'organization',
      'pro ltd': 'organization',
      'pro plus': 'organization',
      'pro plus (early bird)': 'organization',
      'tier 4 ltd': 'organization',
      'premium': 'organization',
      'business': 'organization',
      'enterprise': 'organization',
      'organization': 'organization',
    };

    const targetTier = tierByPlanName[currentPlanNameLower];
    if (targetTier) {
      const tierMatch = plansData.find(
        (p: any) =>
          p.name.toLowerCase() === targetTier
          && p.interval === 'yearly'
          && planSatisfiesFeature(p)
      );
      if (tierMatch) {
        authorizedPlanName = tierMatch.name;
      }
    }
  }

  // Find the plan with the matching name + interval that ALSO satisfies the feature.
  // This disambiguates legacy and new plans that share a name (e.g. legacy Scale vs new Scale)
  // by skipping any version that doesn't carry the feature, then preferring the highest position.
  const planForInterval = plansData
    .filter(
      (p: any) =>
        p.name.toLowerCase() === authorizedPlanName!.toLowerCase()
        && p.interval === interval
        && planSatisfiesFeature(p)
    )
    .sort((a: any, b: any) => b.position - a.position)[0];

  return planForInterval || null;
}

export function shouldSuggestUpgrade(groupData: any, plansData: any): boolean {
  return getNextUpgradePlan(groupData, plansData) !== null;
}

/**
 * Formats a given price. If the price is an integer, it returns the price as a locale-specific string without decimal places.
 * If the price has decimal places, it formats it to two decimal places and then returns it as a locale-specific string.
 * @param originalPrice The price to be formatted.
 * @returns The formatted price as a string.
 */
export const roundPriceToWhole = (originalPrice: any) => {
  return Math.round(originalPrice).toLocaleString();
}

/**
 * Checks if the group's current usage exceeds a plan's limit.
 * Returns true if usage is OVER the limit, false if within limit.
 * -1 means unlimited.
 * 
 * @param usage - Current usage count from group data
 * @param limit - Limit from the plan
 * @returns boolean - true if over limit, false if within limit
 */
export function isOverPlanLimit(usage: number, limit: number): boolean {
  if (limit === -1) return false; // unlimited
  return usage > limit;
}

export interface UsageBlocker {
  type: 'usage' | 'feature';
  key: string;
  currentUsage: any;
  planLimit: any;
  message: string;
}

// Parked: public-visibility downgrade blockers are hidden for now (GP-12936)
// // Every surface that can be made publicly visible. Keys keep the total_ prefix
// // so DowngradeBlockersModal's formatKeyName renders them as "Public Courses".
// const publicVisibilityUsageFields = [
//   { key: 'total_public_channels', label: 'channels' },
//   { key: 'total_public_courses', label: 'courses' },
//   { key: 'total_public_events', label: 'events' },
//   { key: 'total_public_library_content', label: 'resource library items' },
//   { key: 'total_public_newsletters', label: 'newsletters' },
// ];
//
// // Same rule as planBlocksPublicVisibility, but asked of a downgrade target
// // rather than the group's current plan.
// const targetPlanBlocksPublicVisibility = (targetPlan: any) => {
//   return Boolean(
//     targetPlan &&
//     targetPlan.content_visibility &&
//     targetPlan.content_visibility.public === false
//   );
// }
//
// // Only the surfaces that actually have public items, so a downgrade never
// // reports a blocker reading "0".
// function getPublicVisibilityUsage(group: any) {
//   return publicVisibilityUsageFields
//     .map((field) => ({
//       ...field,
//       count: group && group[field.key] ? group[field.key] : 0,
//     }))
//     .filter((field) => field.count > 0);
// }

/**
 * Checks if a group's current usage allows downgrading to a target plan.
 * Uses only the specified fields to check feature usage against plan limits.
 * 
 * @param group - The group data object with usage fields
 * @param targetPlan - The plan the user wants to downgrade to
 * @returns boolean - true if downgrade is allowed, false otherwise
 */
export function canDowngradePlan(group: any, targetPlan: any): boolean {
  if (!group || !targetPlan) {
    return false;
  }

  // Check total_channels against plan.channels
  if (isOverPlanLimit(group.total_channels || 0, targetPlan.channels)) {
    return false;
  }

  // Check total_members against plan.members
  if (isOverPlanLimit(group.total_members || 0, targetPlan.members)) {
    return false;
  }

  // Check total_courses against plan.courses
  if (isOverPlanLimit(group.total_courses || 0, targetPlan.courses)) {
    return false;
  }

  // Check total_published_courses against plan.active_courses
  if (isOverPlanLimit(group.total_published_courses || 0, targetPlan.active_courses)) {
    return false;
  }

  // Check total_created_library_contents against plan.library_content
  if (isOverPlanLimit(group.total_created_library_contents || 0, targetPlan.library_content)) {
    return false;
  }

  // Check total_landing_pages against plan.custom_pages
  if (isOverPlanLimit(group.total_landing_pages || 0, targetPlan.custom_pages)) {
    return false;
  }

  // Check total_workflows against plan.workflows
  if (isOverPlanLimit(group.total_workflows || 0, targetPlan.workflows)) {
    return false;
  }

  // Check total_active_workflows against plan.active_workflows
  if (isOverPlanLimit(group.total_active_workflows || 0, targetPlan.active_workflows)) {
    return false;
  }

  // Check total_teammates against plan.team_size
  if (isOverPlanLimit(group.total_teammates || 0, getAvailableTeamSize(group) !== null ? getAvailableTeamSize(group) : targetPlan.team_size)) {
    return false;
  }

  // Check total_email_identities against plan.email_white_label
  if (isOverPlanLimit(group.total_email_identities || 0, targetPlan.email_white_label)) {
    return false;
  }

  // Check total_published_landing_pages against plan.published_custom_pages
  if (isOverPlanLimit(group.total_published_landing_pages || 0, targetPlan.published_custom_pages)) {
    return false;
  }

  // Check total_published_library_contents against plan.active_library_content
  if (isOverPlanLimit(group.total_published_library_contents || 0, targetPlan.active_library_content)) {
    return false;
  }

  // Check segments: if target plan doesn't support segments, block if usage > 0
  if (targetPlan.admin_tools && targetPlan.admin_tools.segments === false && (group.total_segments || 0) > 0) {
    return false;
  }

  // Check course certificates: if target plan doesn't support certificates, block if usage > 0
  if (targetPlan.learning_tools && targetPlan.learning_tools.course_certificate === false && (group.total_certificates || 0) > 0) {
    return false;
  }

  // Check course active certificates: if target plan doesn't support active certificates, block if false
  if ((group.active_subscription && group.active_subscription.plan && group.active_subscription.plan.active_course_certificate === false) &&
    (group.total_active_certificates || 0) > 0) {
    return false;
  }

  // Check drip courses: if target plan doesn't support drip, block if usage > 0
  if (targetPlan.learning_tools && targetPlan.learning_tools.drip_course === false && (group.total_courses_with_drip || 0) > 0) {
    return false;
  }

  // Check scheduling: if target plan doesn't support scheduling, block if usage > 0
  if (targetPlan.admin_tools && targetPlan.admin_tools.post_and_email_scheduling === false && (group.total_scheduled_messages || 0) > 0) {
    return false;
  }

  // Check group subscriptions: if target plan doesn't support group subscriptions, block if usage > 0
  if (targetPlan.payments && targetPlan.payments.group_subscriptions === false && (group.total_group_plans || 0) > 0) {
    return false;
  }

  // Check active subscription products against plan.active_subscription_products
  if (isOverPlanLimit(group.active_public_plans_count || 0, targetPlan.active_subscription_products)) {
    return false;
  }

  // Check custom_profile_fields: if target plan doesn't support custom_profile_fields, block if usage > 0
  if (targetPlan.admin_tools && targetPlan.admin_tools.custom_profile_fields === false && (group.total_custom_profile_fields || 0) > 0) {
    return false;
  }

  // Parked: public-visibility blockers (GP-12936)
  //   // Check public visibility: if target plan doesn't support public content,
  //   // block while anything is still public
  //   if (targetPlanBlocksPublicVisibility(targetPlan) && getPublicVisibilityUsage(group).length > 0) {
  //     return false;
  //   }

  // powered_by_groupapp = 1 means branding IS showing, = 0 means branding has been REMOVED
  const hasBrandingShowing = group && !!group.powered_by_groupapp

  // Check remove_groupapp_branding: if target plan doesn't support branding removal, block if branding has been removed
  if (targetPlan.branding && targetPlan.branding.remove_groupapp_branding === false && !hasBrandingShowing) {
    return false;
  }

  /**
   * Storage Downgrade Check with Add-on Support
   * 
   * Users can purchase storage add-ons that increase their max_str beyond the plan's base storage_limit.
   * When downgrading, we preserve these add-ons and apply them to the new plan's limit.
   */
  if (targetPlan.storage_limit !== -1 && group.str_usg) {
    const hasStorageAddons = group.max_str; // User has purchased storage add-ons

    // FLOW 1: User does NOT have storage add-ons (no max_str)
    // Can downgrade if: str_usg <= new_plan.storage_limit
    // Example: usage=400MB, new_plan=500MB → Can downgrade
    if (!hasStorageAddons) {
      const limit = targetPlan.storage_limit;
      if (group.str_usg > limit) {
        return false; // Usage exceeds new plan's limit
      }
    } 
    // FLOW 2: User HAS storage add-ons (has max_str)
    // Calculate add-on: addon = max_str - current_plan.storage_limit
    // Effective limit: addon + new_plan.storage_limit
    // Can downgrade if: str_usg <= effective_limit
    // Example: max_str=150,000MB, current_plan=1,000MB → addon=149,000MB
    //          new_plan=500MB → effective_limit=149,500MB
    //          usage=149,000MB → Can downgrade (149,000 <= 149,500)
    else {
      // Safely get current plan's storage limit
      if (group.active_subscription && group.active_subscription.plan && group.active_subscription.plan.storage_limit) {
        const currentPlanStorageLimit = group.active_subscription.plan.storage_limit;
        const addon = group.max_str - currentPlanStorageLimit; // Purchased extra storage
        
        if (addon > 0) {
          const effectiveLimit = addon + targetPlan.storage_limit; // Add-on + new plan's base
          if (group.str_usg > effectiveLimit) {
            return false; // Usage exceeds effective limit (with add-ons)
          }
        } else {
          // No actual add-ons, fall back to Flow 1 logic
          if (group.str_usg > targetPlan.storage_limit) {
            return false;
          }
        }
      } else {
        // Can't determine current plan limit, fall back to Flow 1
        if (group.str_usg > targetPlan.storage_limit) {
          return false;
        }
      }
    }
  }

  // Check custom_domain_status: if "active", plan must support custom_domain
  if (group.custom_domain_status === "active" && targetPlan.branding && !targetPlan.branding.custom_domain) {
    return false;
  }

  // Check sso_enabled: if true (1), plan must support sso
  if (Boolean(group.sso_enabled) && !targetPlan.sso) {
    return false;
  } else if (group.oauth_sso !== null && group.oauth_sso.oauth_provider !== null && group.oauth_sso.oauth_provider.slug === 'yourmembership' && Boolean(group.sso_enabled) && !targetPlan.integration_data.yourmembership) {
    // Check Yourmembership oauth_sso
    return false;
  } else if (group.oauth_sso !== null && group.oauth_sso.oauth_provider !== null && group.oauth_sso.oauth_provider.slug === 'wordpress' && Boolean(group.sso_enabled) && !targetPlan.integration_data.wordpress_sso) {
    // Check Wordpress oauth_sso
    return false;
  } else if (group.oauth_sso !== null && group.oauth_sso.oauth_provider !== null && group.oauth_sso.oauth_provider.slug && Boolean(group.sso_enabled) && !targetPlan.integration_data.custom_sso) {
    // Check Custom SSO
    return false;
  }

  // Check connected_applications against plan.integration_data
  if (group.connected_applications && group.connected_applications.length > 0 && targetPlan.integration_data) {
    for (const app of group.connected_applications) {
      const slug = app.slug;
      // If the integration is not supported (false) in the target plan, block downgrade
      if (targetPlan.integration_data[slug] === false) {
        return false;
      }
    }
  }
   
  // check if community supports sellable_addons
  if (targetPlan.payments && targetPlan.payments.sellable_addons === false && (group.total_sellable_addons || 0) > 0) {
    return false;
  }

  // Check learning_tools.learning_pathway: if false, block if group has learning pathways
  if (targetPlan.learning_tools && targetPlan.learning_tools.learning_pathway === false && (group.total_active_learning_pathways || 0) > 0) {
    return false;
  }

  return true;
}

/**
 * Returns detailed information about what's blocking a plan downgrade based on group usage.
 * Use this to show users exactly which features/limits prevent them from downgrading.
 * 
 * @param group - The group data object with usage fields
 * @param targetPlan - The plan the user wants to downgrade to
 * @returns Array of blockers with details about each blocking usage/feature
 */
export function getDowngradeBlockers(group: any, targetPlan: any): UsageBlocker[] {
  const blockers: UsageBlocker[] = [];

  if (!group || !targetPlan) {
    return blockers;
  }

  // Helper to format key names for display
  const formatKeyName = (key: string): string => {
    return key
      .replace(/_/g, ' ')
      .replace(/-/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Check total_channels against plan.channels
  if (isOverPlanLimit(group.total_channels || 0, targetPlan.channels)) {
    blockers.push({
      type: 'usage',
      key: 'total_channels',
      currentUsage: group.total_channels,
      planLimit: targetPlan.channels,
      message: `You have ${group.total_channels} channels, but the ${targetPlan.name} plan only allows ${targetPlan.channels === -1 ? 'unlimited' : targetPlan.channels}`
    });
  }

  // Check total_members against plan.members
  if (isOverPlanLimit(group.total_members || 0, targetPlan.members)) {
    blockers.push({
      type: 'usage',
      key: 'total_members',
      currentUsage: group.total_members,
      planLimit: targetPlan.members,
      message: `You have ${group.total_members} members, but the ${targetPlan.name} plan only allows ${targetPlan.members === -1 ? 'unlimited' : targetPlan.members}`
    });
  }

    // Check segments
  if (targetPlan.admin_tools && targetPlan.admin_tools.segments === false && (group.total_segments || 0) > 0) {
    blockers.push({
      type: 'feature',
      key: 'total_segments',
      currentUsage: group.total_segments,
      planLimit: 0,
      message: `You have ${group.total_segments} segments, but the ${targetPlan.name} plan does not support segments`
    });
  }

  // Check total_courses against plan.courses
  if (isOverPlanLimit(group.total_courses || 0, targetPlan.courses)) {
    blockers.push({
      type: 'usage',
      key: 'total_courses',
      currentUsage: group.total_courses,
      planLimit: targetPlan.courses,
      message: `You have ${group.total_courses} courses, but the ${targetPlan.name} plan only allows ${targetPlan.courses === -1 ? 'unlimited' : targetPlan.courses}`
    });
  }

  // Check total_published_courses against plan.active_courses
  if (isOverPlanLimit(group.total_published_courses || 0, targetPlan.active_courses)) {
    blockers.push({
      type: 'usage',
      key: 'total_published_courses',
      currentUsage: group.total_published_courses,
      planLimit: targetPlan.active_courses,
      message: `You have ${group.total_published_courses} published courses, but the ${targetPlan.name} plan only allows ${targetPlan.active_courses === -1 ? 'unlimited' : targetPlan.active_courses}`
    });
  }

  // Check drip courses
  if (targetPlan.learning_tools && targetPlan.learning_tools.drip_course === false && (group.total_courses_with_drip || 0) > 0) {
    blockers.push({
      type: 'feature',
      key: 'total_courses_with_drip',
      currentUsage: group.total_courses_with_drip,
      planLimit: 0,
      message: `You have ${group.total_courses_with_drip} courses with drip content, but the ${targetPlan.name} plan does not support drip courses`
    });
  }

  // Check course certificates
  if (targetPlan.learning_tools && targetPlan.learning_tools.course_certificate === false && (group.total_certificates || 0) > 0) {
    blockers.push({
      type: 'feature',
      key: 'total_certificates',
      currentUsage: group.total_certificates,
      planLimit: 0,
      message: `You have ${group.total_certificates} certificates, but the ${targetPlan.name} plan does not support course certificates`
    });
  }

  // Check active course certificates
  if (targetPlan.active_course_certificates === false && (group.total_active_certificates || 0) > 0) {
    blockers.push({
      type: 'feature',
      key: 'active_course_certificates',
      currentUsage: group.total_active_certificates,
      planLimit: 0,
      message: `You have ${group.total_active_certificates} active certificates, but the ${targetPlan.name} plan does not support active course certificates`
    });
  }


  // Check total_created_library_contents against plan.library_content
  if (isOverPlanLimit(group.total_created_library_contents || 0, targetPlan.library_content)) {
    blockers.push({
      type: 'usage',
      key: 'library_content',
      currentUsage: group.total_created_library_contents,
      planLimit: targetPlan.library_content,
      message: `You have ${group.total_created_library_contents} library contents, but the ${targetPlan.name} plan only allows ${targetPlan.library_content === -1 ? 'unlimited' : targetPlan.library_content}`
    });
  }

  // Check total_published_library_contents against plan.active_library_content
  if (isOverPlanLimit(group.total_published_library_contents || 0, targetPlan.active_library_content)) {
    blockers.push({
      type: 'usage',
      key: 'total_published_library_contents',
      currentUsage: group.total_published_library_contents,
      planLimit: targetPlan.active_library_content,
      message: `You have ${group.total_published_library_contents} published library contents, but the ${targetPlan.name} plan only allows ${targetPlan.active_library_content === -1 ? 'unlimited' : targetPlan.active_library_content}`
    });
  }

  // Check total_landing_pages against plan.custom_pages
  if (isOverPlanLimit(group.total_landing_pages || 0, targetPlan.custom_pages)) {
    blockers.push({
      type: 'usage',
      key: 'total_landing_pages',
      currentUsage: group.total_landing_pages,
      planLimit: targetPlan.custom_pages,
      message: `You have ${group.total_landing_pages} landing pages, but the ${targetPlan.name} plan only allows ${targetPlan.custom_pages === -1 ? 'unlimited' : targetPlan.custom_pages}`
    });
  }


    // Check total_published_landing_pages against plan.published_custom_pages
  if (isOverPlanLimit(group.total_published_landing_pages || 0, targetPlan.published_custom_pages)) {
    blockers.push({
      type: 'usage',
      key: 'total_published_landing_pages',
      currentUsage: group.total_published_landing_pages,
      planLimit: targetPlan.published_custom_pages,
      message: `You have ${group.total_published_landing_pages} published landing pages, but the ${targetPlan.name} plan only allows ${targetPlan.published_custom_pages === -1 ? 'unlimited' : targetPlan.published_custom_pages}`
    });
  }

  // Check total_workflows against plan.workflows
  if (isOverPlanLimit(group.total_workflows || 0, targetPlan.workflows)) {
    blockers.push({
      type: 'usage',
      key: 'total_workflows',
      currentUsage: group.total_workflows,
      planLimit: targetPlan.workflows,
      message: `You have ${group.total_workflows} workflows, but the ${targetPlan.name} plan only allows ${targetPlan.workflows === -1 ? 'unlimited' : targetPlan.workflows}`
    });
  }

  // Check total_active_workflows against plan.active_workflows
  if (isOverPlanLimit(group.total_active_workflows || 0, targetPlan.active_workflows)) {
    blockers.push({
      type: 'usage',
      key: 'total_active_workflows',
      currentUsage: group.total_active_workflows,
      planLimit: targetPlan.active_workflows,
      message: `You have ${group.total_active_workflows} active workflows, but the ${targetPlan.name} plan only allows ${targetPlan.active_workflows === -1 ? 'unlimited' : targetPlan.active_workflows}`
    });
  }

  // Check total_teammates against plan.team_size
  const availableTeamSize = getAvailableTeamSize(group);
  if (isOverPlanLimit(group.total_teammates || 0, availableTeamSize !== null ? availableTeamSize : targetPlan.team_size)) {
    blockers.push({
      type: 'usage',
      key: 'total_teammates',
      currentUsage: group.total_teammates,
      planLimit:  availableTeamSize !== null ? availableTeamSize : targetPlan.team_size,
      message: `You have ${group.total_teammates} teammates, but the ${targetPlan.name} plan only allows ${targetPlan.team_size === -1 ? 'unlimited' : targetPlan.team_size}`
    });
  }

  // Check powered_by_groupapp: 1 = branding showing, 0 = branding removed
  const hasBrandingShowing = group && !!group.powered_by_groupapp
  if (targetPlan.branding && targetPlan.branding.remove_groupapp_branding === false && !hasBrandingShowing) {
    blockers.push({
      type: 'feature',
      key: '"We use GroupApp" branding',
      currentUsage: 'disabled',
      planLimit: 'enabled',
      message: `You have removed the "Powered by GroupApp" branding, but the ${targetPlan.name} plan does not support removing GroupApp branding`
    });
  }

  // Check scheduling
  if (targetPlan.admin_tools && targetPlan.admin_tools.post_and_email_scheduling === false && (group.total_scheduled_messages || 0) > 0) {
    blockers.push({
      type: 'feature',
      key: 'Post & Email Scheduling',
      currentUsage: group.total_scheduled_messages,
      planLimit: 0,
      message: `You have ${group.total_scheduled_messages} scheduled messages, but the ${targetPlan.name} plan does not support post and email scheduling`
    });
  }

  // Check custom_profile_fields
  if (targetPlan.admin_tools && targetPlan.admin_tools.custom_profile_fields === false && (group.total_custom_profile_fields || 0) > 0) {
    blockers.push({
      type: 'feature',
      key: 'custom_profile_fields',
      currentUsage: group.total_custom_profile_fields,
      planLimit: 0,
      message: `You have ${group.total_custom_profile_fields} custom profile fields, but the ${targetPlan.name} plan does not support custom profile fields`
    });
  }

  // Parked: public-visibility blockers (GP-12936)
  //   // Check public visibility: one row per surface that still has public items
  //   if (targetPlanBlocksPublicVisibility(targetPlan)) {
  //     getPublicVisibilityUsage(group).forEach((field) => {
  //       blockers.push({
  //         type: 'feature',
  //         key: field.key,
  //         currentUsage: field.count,
  //         planLimit: 0,
  //         message: `You have ${field.count} ${field.label} set to public visibility, but the ${targetPlan.name} plan does not support public content`
  //       });
  //     });
  //   }

  // Check receive webhooks
  if (targetPlan.integration_data && targetPlan.integration_data.receive_webhook === false && (group.total_workflow_webhook_receive || 0) > 0) {
    blockers.push({
      type: 'feature',
      key: 'Receive Webhook trigger in workflows',
      currentUsage: group.total_workflow_webhook_receive,
      planLimit: 'not supported',
      message: `You have ${group.total_workflow_webhook_receive} receive webhooks, but the ${targetPlan.name} plan does not support receive webhooks`
    });
  }

  // Check send webhooks
  if (targetPlan.integration_data && targetPlan.integration_data.send_webhook === false && (group.total_workflow_webhook_send || 0) > 0) {
    blockers.push({
      type: 'feature',
      key: 'Send Webhook action in workflows',
      currentUsage: group.total_workflow_webhook_send,
      planLimit: 'not supported',
      message: `You have ${group.total_workflow_webhook_send} send webhooks, but the ${targetPlan.name} plan does not support send webhooks`
    });
  }


  // Check total_email_identities against plan.email_white_label
  if (isOverPlanLimit(group.total_email_identities || 0, targetPlan.email_white_label)) {
    blockers.push({
      type: 'usage',
      key: 'Email White-labeling',
      currentUsage: 'configured',
      planLimit: 'not supported',
      message: `You have ${group.total_email_identities} email identities, but the ${targetPlan.name} plan only allows ${targetPlan.email_white_label === -1 ? 'unlimited' : targetPlan.email_white_label}`
    });
  }

  // Check group subscriptions
  if (targetPlan.payments && targetPlan.payments.group_subscriptions === false && (group.total_group_plans || 0) > 0) {
    blockers.push({
      type: 'feature',
      key: 'Group Subscriptions',
      currentUsage: group.total_group_plans,
      planLimit: 'not supported',
      message: `You have ${group.total_group_plans} group plans, but the ${targetPlan.name} plan does not support group subscriptions`
    });
  }

  // Check active subscription products against plan.active_subscription_products
  if (isOverPlanLimit(group.active_public_plans_count || 0, targetPlan.active_subscription_products)) {
    blockers.push({
      type: 'usage',
      key: 'active_subscription_products',
      currentUsage: group.active_public_plans_count,
      planLimit: targetPlan.active_subscription_products,
      message: `You have ${group.active_public_plans_count} active subscription plans, but the ${targetPlan.name} plan only allows ${targetPlan.active_subscription_products === -1 ? 'unlimited' : targetPlan.active_subscription_products}`
    });
  }

  /**
   * Storage Downgrade Check with Add-on Support
   * 
   * Users can purchase storage add-ons that increase their max_str beyond the plan's base storage_limit.
   * When downgrading, we preserve these add-ons and apply them to the new plan's limit.
   */
  if (targetPlan.storage_limit !== -1 && group.str_usg) {
    const hasStorageAddons = group.max_str; // User has purchased storage add-ons

    // FLOW 1: User does NOT have storage add-ons (no max_str)
    // Can downgrade if: str_usg <= new_plan.storage_limit
    if (!hasStorageAddons) {
      const limit = targetPlan.storage_limit;
      if (group.str_usg > limit) {
           const formatter = new Intl.NumberFormat("en-US", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
        });
        const currentUsageGB = formatter.format(group.str_usg / 1000);
        const limitGB = Math.floor(limit / 1000);
        blockers.push({
          type: 'usage',
          key: 'storage',
          currentUsage: `${currentUsageGB} GB`,
          planLimit: `${limitGB} GB`,
          message: `You are using ${currentUsageGB} GB of storage, but the ${targetPlan.name} plan only allows ${limitGB} GB`
        });
      }
    } 
    // FLOW 2: User HAS storage add-ons (has max_str)
    // Calculate add-on: addon = max_str - current_plan.storage_limit
    // Effective limit: addon + new_plan.storage_limit
    // Can downgrade if: str_usg <= effective_limit
    else {
      // Safely get current plan's storage limit
      if (group.active_subscription && group.active_subscription.plan && group.active_subscription.plan.storage_limit) {
        const currentPlanStorageLimit = group.active_subscription.plan.storage_limit;
        const addon = group.max_str - currentPlanStorageLimit; // Purchased extra storage
        
        if (addon > 0) {
          const effectiveLimit = addon + targetPlan.storage_limit; // Add-on + new plan's base
          if (group.str_usg > effectiveLimit) {
                 const formatter = new Intl.NumberFormat("en-US", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
        });
            const currentUsageGB = formatter.format(group.str_usg / 1000);
            const effectiveLimitGB = Math.floor(effectiveLimit / 1000);
            blockers.push({
              type: 'usage',
              key: 'storage',
              currentUsage: `${currentUsageGB} GB`,
              planLimit: `${effectiveLimitGB} GB`,
              message: `You are using ${currentUsageGB} GB of storage, but with your add-ons, the ${targetPlan.name} plan only allows ${effectiveLimitGB} GB`
            });
          }
        } else {
          // No actual add-ons, fall back to Flow 1 logic
          if (group.str_usg > targetPlan.storage_limit) {
              const formatter = new Intl.NumberFormat("en-US", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
                });
            const currentUsageGB = formatter.format(group.str_usg / 1000);
            const limitGB = Math.floor(targetPlan.storage_limit / 1000);
            blockers.push({
              type: 'usage',
              key: 'storage',
              currentUsage: `${currentUsageGB} GB`,
              planLimit: `${limitGB} GB`,
              message: `You are using ${currentUsageGB} GB of storage, but the ${targetPlan.name} plan only allows ${limitGB} GB`
            });
          }
        }
      } else {
        // Can't determine current plan limit, fall back to Flow 1
        if (group.str_usg > targetPlan.storage_limit) {
           const formatter = new Intl.NumberFormat("en-US", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
                });
          const currentUsageGB = formatter.format(group.str_usg / 1000);
          const limitGB = Math.floor(targetPlan.storage_limit / 1000);
          blockers.push({
            type: 'usage',
            key: 'storage',
            currentUsage: `${currentUsageGB} GB`,
            planLimit: `${limitGB} GB`,
            message: `You are using ${currentUsageGB} GB of storage, but the ${targetPlan.name} plan only allows ${limitGB} GB`
          });
        }
      }
    }
  }

  // Check custom_domain_status
  if (group.custom_domain_status === "active" && targetPlan.branding && !targetPlan.branding.custom_domain) {
    blockers.push({
      type: 'feature',
      key: 'custom_domain',
      currentUsage: group.custom_domain_status,
      planLimit: 'not supported',
      message: `You have an active custom domain, but the ${targetPlan.name} plan does not support custom domains`
    });
  }

  // Check sso_enabled (1 = true, 0 = false)
  if (Boolean(group.sso_enabled) && !targetPlan.sso) {
    blockers.push({
      type: 'feature',
      key: 'SSO',
      currentUsage: 'enabled',
      planLimit: 'not supported',
      message: `You have SSO enabled, but the ${targetPlan.name} plan does not support SSO`
    });
  } else  if (group.oauth_sso !== null && group.oauth_sso.oauth_provider !== null && group.oauth_sso.oauth_provider.slug === 'yourmembership' && Boolean(group.sso_enabled) && !targetPlan.integration_data.yourmembership) {
     // Check Yourmemberhsip oauth_sso
    blockers.push({
      type: 'feature',
      key: 'Yourmembership SSO',
      currentUsage: 'configured',
      planLimit: 'not supported',
      message: `You have Yourmembership SSO configured, but the ${targetPlan.name} plan does not support Yourmembership SSO`
    });
  } else if (group.oauth_sso !== null && group.oauth_sso.oauth_provider !== null && group.oauth_sso.oauth_provider.slug === 'wordpress' && Boolean(group.sso_enabled) && !targetPlan.integration_data.wordpress_sso) {
    // Check Wordpress oauth_sso
    blockers.push({
      type: 'feature',
      key: 'Wordpress SSO',
      currentUsage: 'configured',
      planLimit: 'not supported',
      message: `You have Wordpress SSO configured, but the ${targetPlan.name} plan does not support Wordpress SSO`
    });
  } else if (group.oauth_sso !== null && group.oauth_sso.oauth_provider !== null && group.oauth_sso.oauth_provider.slug && Boolean(group.sso_enabled) && !targetPlan.integration_data.custom_sso) {
    // Check Memberpress oauth_sso
    blockers.push({
      type: 'feature',
      key: 'Custom SSO',
      currentUsage: 'configured',
      planLimit: 'not supported',
      message: `You have Custom SSO configured, but the ${targetPlan.name} plan does not support Custom SSO`
    });
  }

  // Check connected_applications
  if (group.connected_applications && group.connected_applications.length > 0 && targetPlan.integration_data) {
    for (const app of group.connected_applications) {
      const slug = app.slug;
      if (targetPlan.integration_data[slug] === false) {
        blockers.push({
          type: 'feature',
          key: `integration_${slug}`,
          currentUsage: 'connected',
          planLimit: 'not supported',
          message: `You have ${formatKeyName(slug)} connected, but the ${targetPlan.name} plan does not support this integration`
        });
      }
    }
  }

  // Check sellable add-ons
  if (targetPlan.payments && targetPlan.payments.sellable_addons === false && (group.total_sellable_addons || 0) > 0) {
    blockers.push({
      type: 'feature',
      key: 'Sellable Add-ons',
      currentUsage: group.total_sellable_addons,
      planLimit: 'not supported',
      message: `You have ${group.total_sellable_addons} sellable add-ons, but the ${targetPlan.name} plan does not support sellable add-ons`
    });
  }

  // Check learning_tools.learning_pathway
  if (targetPlan.learning_tools && targetPlan.learning_tools.learning_pathway === false && (group.total_active_learning_pathways || 0) > 0) {
    blockers.push({
      type: 'feature',
      key: 'Active Learning Pathways',
      currentUsage: group.total_active_learning_pathways,
      planLimit: 'not supported',
      message: `You have ${group.total_active_learning_pathways} learning pathways, but the ${targetPlan.name} plan does not support learning pathways`
    });
  }

  return blockers;
}

function getAutoFontColor(bgColor: string, branding?: any) {
  if (!bgColor) return;

  // Preset palettes (any branding whose `name` is not "custom") are designed to
  // pair with white text, so we skip the luminance calc for them and always
  // return white. Only user-picked "custom" colors fall through to the
  // automatic contrast calculation below.
  if (branding && branding.name && String(branding.name).toLowerCase() !== 'custom') {
    return '#ffffff';
  }

  // Remove "#"
  const hex = bgColor.replace('#', '');

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Convert RGB to linear RGB (gamma correction)
  const toLinear = (channel: number) => {
    const normalized = channel / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : Math.pow((normalized + 0.055) / 1.055, 2.4);
  };

  const rLinear = toLinear(r);
  const gLinear = toLinear(g);
  const bLinear = toLinear(b);

  // Calculate relative luminance (WCAG formula)
  const luminance = 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;

  // Threshold biased toward WHITE text. The strict WCAG max-contrast crossover
  // (0.179) tips saturated mid-tones — blues/purples especially, since blue is
  // weighted only 0.0722 — to black even though white reads far better on them
  // (e.g. #7c71f0 L≈0.22, rgb(66,125,173) L≈0.19, #c09fc7 L≈0.40). A 0.5 cut
  // keeps genuinely light colors on black (e.g. #FFD93D L≈0.71) while giving
  // white to medium-intensity brand colors. Mirrors themeTokens/colorTokens onColor.
  return luminance > 0.5 ? '#000000' : '#ffffff';
}

export const getHeaderChallenge = async (visitorId: string, fallbackChallenge?: string) => {
  const challengeMeta: any = document.querySelector('meta[name="challenge"]');
  let challenge = ""

  if(challengeMeta) {
    challenge = decodeURIComponent(challengeMeta.getAttribute('content') || "");
  }

  if(!challenge || challenge === "null" || challenge === "undefined") {
    challenge = fallbackChallenge || "";
  }

  if(!challenge) {
    return ""
  }

  try {
    const hash = await hmacSha256(challenge, visitorId);
  return hash;
  } catch(error) {
    return ""
  }
 }

async function hmacSha256(secretKey: string, string1: string) {
  // 1. Combine strings deterministically
  const message = `${string1}`;

  // 2. Encode key and message
  const encoder = new TextEncoder();
  const keyData = encoder.encode(secretKey);
  const messageData = encoder.encode(message);

  // 3. Import the secret key for HMAC-SHA256
  const cryptoKey = await window.crypto.subtle.importKey(
    "raw",
    keyData,
    { name: "HMAC", hash: { name: "SHA-256" } },
    false, // not extractable
    ["sign"]
  );

  // 4. Sign (compute HMAC)
  const signature = await window.crypto.subtle.sign("HMAC", cryptoKey, messageData);

  // 5. Convert ArrayBuffer to hex
  const hashArray = Array.from(new Uint8Array(signature));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, "0")).join("");

  return hashHex;
}

/**
 * Creates a subtle gradient from a channel sidebar color and brand color.
 */
export function createGradient(
  baseColor: string,
  windowColor: string,
  mode?: "top" | "bottom" | "bottom-s"
) {
  // Extract alpha channel from hex colors
  const getAlpha = (hex: string): number => {
    const cleanHex = hex.replace("#", "");
    if (cleanHex.length === 8) {
      return parseInt(cleanHex.slice(6, 8), 16) / 255; // Convert to 0-1 range
    }
    return 1; // Default to fully opaque
  };

  // Shared helper functions
  const hexToRgb = (hex: string) => {
    // Support both 6-character (#RRGGBB) and 8-character (#RRGGBBAA) hex codes
    // Extract only RGB, ignore alpha if present
    hex = hex.replace("#", "").slice(0, 6);
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
    };
  };

  const rgbToHex = (r: number, g: number, b: number, alpha?: number) => {
    const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
    const rgbHex =
      "#" +
      ((1 << 24) | (clamp(r) << 16) | (clamp(g) << 8) | clamp(b))
        .toString(16)
        .slice(1);
    
    // Append alpha if provided
    if (alpha !== undefined && alpha < 1) {
      const alphaHex = Math.round(alpha * 255).toString(16).padStart(2, '0');
      return `${rgbHex}${alphaHex}`;
    }
    return rgbHex;
  };

  const blend = (a: string, b: string, t: number) => {
    const c1 = hexToRgb(a);
    const c2 = hexToRgb(b);
    
    // Blend alpha channels proportionally
    const a1 = getAlpha(a);
    const a2 = getAlpha(b);
    const blendedAlpha = a1 + (a2 - a1) * t;
    
    return rgbToHex(
      c1.r + (c2.r - c1.r) * t,
      c1.g + (c2.g - c1.g) * t,
      c1.b + (c2.b - c1.b) * t,
      blendedAlpha
    );
  };

  // Top mode: horizontal gradient for topbar
  if (mode === "top") {
    const point1 = baseColor;
    const point2 = blend(baseColor, windowColor, 0.20);
    const point3 = blend(baseColor, windowColor, 0.33);
    const point4 = blend(baseColor, windowColor, 0.66);

    return `linear-gradient(
      90deg,
      ${point1} 10%,
      ${point2} 60%,
      ${point3} 70%,
      ${point4} 90%
    )`;
  }

  // Shared gradient variables for default and bottom modes
  const topBar = blend(baseColor, "#000000", 0.18); // dense + cool
  const upper  = blend(baseColor, "#000000", 0.06); // stays cool
  const mid    = blend(baseColor, windowColor, 0.22); // neutral bridge
  const lower  = blend(baseColor, windowColor, 0.45); // warmth introduced late
  const bottom = blend(windowColor, "#000000", 0.04); // grounded, not bright
  const sideShadow = blend(baseColor, "#000000", 0.26);

     // Bottom mode: sidebar gradient with bottom fade
  if (mode === "bottom-s") {

    return `
      linear-gradient(
        180deg,
        ${topBar} 10%,
        ${mid} 30%,
        ${lower} 50%,
        ${bottom} 80%,
      ),
      linear-gradient(
        90deg,
        ${sideShadow} 0%,
        transparent 0%
      ),
      linear-gradient(
        0deg,
        ${sideShadow} 0%,
        transparent 90%
      )
    `;
  }

  // Bottom mode: sidebar gradient with bottom fade
   if (mode === "bottom") {
    const _topBar = blend(baseColor, "#000000", 0.00); // dense + cool

    return `
      linear-gradient(
        180deg,
        ${_topBar} 20%,
        ${upper} 34%,
        ${mid} 55%,
        ${lower} 78%,
        ${bottom} 100%
      ),
      linear-gradient(
        90deg,
        ${sideShadow} 0%,
        transparent 0%
      ),
      linear-gradient(
        0deg,
        ${sideShadow} 0%,
        transparent 0%
      )
    `;
  }

  // Default mode: standard gradient
  return `
    linear-gradient(
      180deg,
      ${topBar} 0%,
      ${upper} 32%,
      ${mid} 58%,
      ${lower} 74%,
      ${bottom} 100%
    ),
    linear-gradient(
      90deg,
      ${sideShadow} 0%,
      transparent 22%
    )
  `;
}


/**
 * Generate background color from a base hex color.
 *
 *
 * @param hex              - Base color in hex format. Supports #RRGGBB and #RRGGBBAA.
 *                           The alpha channel, if present, is preserved in the output.
 * @param darkenBackground - Optional darkening amount in the range [0, 1].
 *                           0 = no change, 1 = black.
 *                           Suggested values: 0.15 subtle · 0.3 moderate · 0.5 strong.
 *                           When omitted, the color is lightened by 85% toward white instead.
 * @returns Hex color string in the same format as the input (#RRGGBB or #RRGGBBAA).
 */
export const darkenBackground = (hex: string, darkenBackground?: number): string => {
  type RGB = { r: number; g: number; b: number };

    /** Extracts the two-character alpha suffix from an 8-digit hex string, or returns `""`. */
  function extractAlpha(hex: string): string {
    if (!hex) return '';
    const digits = hex.replace('#', '');
    return digits.length === 8 ? digits.slice(6, 8) : '';
  }

    /** Parses a 6- or 8-digit hex color string into an RGB object (alpha ignored). */
    function hexToRgb(hex: string): RGB {
      if (!hex) return { r: 0, g: 0, b: 0 };
      const digits = hex.replace('#', '').slice(0, 6);
      const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(digits);

      if (!result) {
        throw new Error(
          `Invalid hex color: "${hex}". Expected format: #RRGGBB or #RRGGBBAA.`
        );
      }

      return {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      };
    }

    /** Serialises an RGB object back to a hex string, re-appending the original alpha if any. */
    function rgbToHex(rgb: RGB, alphaChannel: string): string {
      const toHexByte = (n: number) => Math.round(n).toString(16).padStart(2, '0');
      const base = `#${toHexByte(rgb.r)}${toHexByte(rgb.g)}${toHexByte(rgb.b)}`;
      return alphaChannel ? `${base}${alphaChannel}` : base;
    }

    /** Blends `rgb` toward white by `amount` (0 = unchanged, 1 = white). */
    function lighten(rgb: RGB, amount: number): RGB {
      return {
        r: rgb.r + (255 - rgb.r) * amount,
        g: rgb.g + (255 - rgb.g) * amount,
        b: rgb.b + (255 - rgb.b) * amount,
  };
    }

    /** Blends `rgb` toward black by `amount` (0 = unchanged, 1 = black). */
    function darken(rgb: RGB, amount: number): RGB {
      return {
        r: rgb.r * (1 - amount),
        g: rgb.g * (1 - amount),
        b: rgb.b * (1 - amount),
    };
  }

  const alphaChannel = extractAlpha(hex);
  const baseRgb = hexToRgb(hex);

  const resultRgb =
    darkenBackground !== undefined
      ? darken(baseRgb, darkenBackground)
      : lighten(baseRgb, 0.85);

  return rgbToHex(resultRgb, alphaChannel);
};

const getAvailableTeamSize = (groupData: any) => {
  if (!groupData) return null;
  const availableTeamSize = groupData && groupData.available_team_size;
  const effectiveTeamSize = availableTeamSize !== null && availableTeamSize !== undefined && availableTeamSize !== '' && Number(availableTeamSize) !== 0
  if (effectiveTeamSize) {
    return Number(availableTeamSize);
  }
  return null;
}


const truncateHTML = (html: string, ratio: number = 0.3, maxLength = 600) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const body: any = doc.body;

  const totalText = body.textContent.length;
  const computedLimit = Math.floor(totalText * ratio);
  const limit = Math.min(computedLimit, maxLength);

  let count = 0;

  function truncateNode(node: any) {
    if (count >= limit) return null;

    // TEXT NODE
    if (node.nodeType === Node.TEXT_NODE) {
      const remaining = limit - count;
      const text = node.textContent;

      if (text.length <= remaining) {
        count += text.length;
        return text;
      } else {
        count += remaining;

        // Optional: avoid cutting mid-word
        let sliced = text.slice(0, remaining);
        const lastSpace = sliced.lastIndexOf(" ");
        if (lastSpace > 0) {
          sliced = sliced.slice(0, lastSpace);
        }

        return sliced + "...";
      }
    }

    // ELEMENT NODE
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tagName = node.tagName.toLowerCase();

      let result = `<${tagName}`;

      // Preserve attributes
      for (let attr of node.attributes) {
        result += ` ${attr.name}="${attr.value}"`;
      }
      result += `>`;

      for (let child of node.childNodes) {
        if (count >= limit) break;

        const truncatedChild = truncateNode(child);
        if (truncatedChild) {
          result += truncatedChild;
        }
      }

      result += `</${tagName}>`;
      return result;
    }

    return "";
  }

  let output = "";
  for (let child of body.childNodes) {
    if (count >= limit) break;

    const truncated = truncateNode(child);
    if (truncated) {
      output += truncated;
    }
  }

  return output.replace(/<p>\s*((<br\s*\/?>\s*(<\/br>\s*)?)|&nbsp;|\s)*<\/p>/gi, "");
}

// Fills null or missing fields in a raw Draft.js object so convertFromRaw and truncateDraftJs accept it.
const normalizeDraftRaw = (raw: any): any => {
  // Unwraps Draft.js JSON that was encoded as a JSON string more than once.
  for (let depth = 0; typeof raw === "string" && depth < 5; depth++) {
    try {
      raw = JSON.parse(raw);
    } catch (error) {
      console.warn("Draft.js JSON holds plain text:", error);
      raw = { blocks: [{ text: raw, type: "unstyled" }] };
    }
  }
  const source = raw && typeof raw === "object" ? raw : {};
  const blocks = (Array.isArray(source.blocks) ? source.blocks : [])
    .filter((block: any) => block && typeof block === "object")
    .map((block: any) => ({
      ...block,
      text: typeof block.text === "string" ? block.text : "",
      inlineStyleRanges: Array.isArray(block.inlineStyleRanges) ? block.inlineStyleRanges : [],
      entityRanges: Array.isArray(block.entityRanges) ? block.entityRanges : [],
    }));

  return {
    ...source,
    blocks: blocks.length ? blocks : [{ text: "", type: "unstyled", inlineStyleRanges: [], entityRanges: [] }],
    entityMap: source.entityMap && typeof source.entityMap === "object" ? source.entityMap : {},
  };
};

// True when the Draft.js object has no text and no media blocks.
const isEmptyDraftRaw = (raw: any): boolean =>
  raw.blocks.every((block: any) => block.type === "unstyled" && !block.text.trim());

// Parses saved Draft.js JSON; malformed JSON gives one empty block.
const parseDraftJson = (draftJSON: any): any => {
  if (typeof draftJSON !== "string") return normalizeDraftRaw(draftJSON);
  let raw = null;
  try {
    raw = JSON.parse(draftJSON);
  } catch (error) {
    console.warn("Invalid Draft.js JSON:", error);
  }

  return normalizeDraftRaw(raw);
};

const truncateDraftJs = (draftJSON: any, options = {}) => {
  if (!(draftJSON && draftJSON.blocks)) return draftJSON;

  const {
    maxBlocks = 3,
    singleBlockRatio = 0.5 // 50% of text if only one block
  }: any = options;

  const blocks = draftJSON.blocks;

  // Remove leading empty blocks
  const meaningfulBlocks = blocks.filter((b: any) => b.text.trim().length > 0);

  if (meaningfulBlocks.length === 0) {
    return { blocks: [], entityMap: {} };
  }

  const usedEntityKeys = new Set();
  let newBlocks = [];

  // 👉 CASE 1: Only ONE meaningful block → truncate it
  if (meaningfulBlocks.length === 1) {
    const block = meaningfulBlocks[0];
    const limit = Math.floor(block.text.length * singleBlockRatio);

    const newText = block.text.slice(0, limit);

    const newInlineStyles = block.inlineStyleRanges
      .filter((r: any) => r.offset < limit)
      .map((r: any) => ({
        ...r,
        length: Math.min(r.length, limit - r.offset)
      }));

    const newEntityRanges = block.entityRanges
      .filter((r: any) => r.offset < limit)
      .map((r: any) => {
        usedEntityKeys.add(String(r.key));
        return {
          ...r,
          length: Math.min(r.length, limit - r.offset)
        };
      });

    newBlocks.push({
      ...block,
      text: newText,
      inlineStyleRanges: newInlineStyles,
      entityRanges: newEntityRanges
    });
  } else {
    // 👉 CASE 2: Multiple blocks → take first N meaningful ones
    let count = 0;

    for (const block of blocks) {
      if (count >= maxBlocks) break;

      if (block.text.trim().length === 0) {
        if (newBlocks.length > 0) {
          newBlocks.push({
            ...block,
            inlineStyleRanges: block.inlineStyleRanges.map((r: any) => ({ ...r })),
            entityRanges: block.entityRanges.map((r: any) => ({ ...r }))
          });
        }
        continue;
      }

      const cloned = {
        ...block,
        inlineStyleRanges: block.inlineStyleRanges.map((r: any) => ({ ...r })),
        entityRanges: block.entityRanges.map((r: any) => ({ ...r }))
      };

      cloned.entityRanges.forEach((r: any) => usedEntityKeys.add(String(r.key)));

      newBlocks.push(cloned);
      count++;
    }
  }

  // rebuild entityMap
  const newEntityMap: any = {};
  Object.keys(draftJSON.entityMap).forEach((key: any) => {
    if (usedEntityKeys.has(key)) {
      newEntityMap[key] = draftJSON.entityMap[key];
    }
  });

  return {
    blocks: newBlocks,
    entityMap: newEntityMap
  };
}

const getFirstWordsFromHTML = (html: string, wordCount = 30) => {
  if (!html || typeof html !== "string") return "";

  // 1. Remove HTML tags
  let text = html.replace(/<[^>]*>/g, "");

  // 2. Decode common HTML entities
  text = text
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");

  // 3. Normalize whitespace
  text = text.replace(/\s+/g, " ").trim();

  // 4. Split into words
  const words = text.split(" ");

  // 5. Return early if not exceeding word count
  if (words.length <= wordCount) {
    return text;
  }

  // 6. Take first N words and append ellipsis
  return words.slice(0, wordCount).join(" ") + "...";
};

const groupHasMobileAddOn = (group: any) =>
  Boolean(
    group &&
    group.add_on_subscriptions &&
    group.add_on_subscriptions.some(
      (sub: any) =>
        sub.type &&
        sub.type.toLowerCase().indexOf('mobile') !== -1 &&
        (sub.stripe_status === 'active' || sub.ended_at)
    )
  );

export interface IScheduledMessagesBlocker {
  reason: 'unavailable' | 'limit';
  feature: string;
}

/**
 * Paid plans go by admin_tools.post_and_email_scheduling for both actions; free
 * plans go by the `scheduled_messages` / `published_scheduled_messages` limits
 * (0 = not included, -1 = unlimited).
 */
export function getScheduledMessagesBlocker(
  group: any,
  action: 'create' | 'publish' = 'create',
  scheduledMessagesCount?: number | null
): IScheduledMessagesBlocker | null {
  const plan = getGroupPlan(group);

  if (!isGroupOnFreePlan(group)) {
    return plan.admin_tools && plan.admin_tools.post_and_email_scheduling === false
      ? { reason: 'unavailable', feature: 'admin_tools.post_and_email_scheduling' }
      : null;
  }

  if (action === 'publish') {
    return plan.published_scheduled_messages === 0
      ? { reason: 'unavailable', feature: 'published_scheduled_messages' }
      : null;
  }

  const limit = plan.scheduled_messages;

  if (limit === 0) {
    return { reason: 'unavailable', feature: 'scheduled_messages' };
  }

  const usage = typeof scheduledMessagesCount === 'number'
    ? scheduledMessagesCount
    : ((group && group.total_scheduled_messages) || 0);

  if (typeof limit === 'number' && limit !== -1 && usage >= limit) {
    return { reason: 'limit', feature: 'scheduled_messages' };
  }

  return null;
}

// Short post/comment age ("5 min ago", "in 2 hr"); "just now" within a minute either side of now.
const shortTimeAgo = (date: string) => {
  if (!date) {
    return '';
  }
  const then = dayjs.utc(date);
  if (!then.isValid()) {
    return '';
  }
  if (Math.abs(dayjs.utc().diff(then, 'second')) < 60) {
    return 'just now';
  }
  return then.locale('en-short').fromNow();
};

export default {
  isAdmin,
  isOwner,
  isModerator,
  isTeamMember,
  canStartDirectMessages,
  canDirectMessage,
  directMessageBlockedReason,
  hasActiveTranscriptionAddOn,
  logger,
  getRandomColor,
  getBillingFrequency,
  prepareGroupPlans,
  updateObject,
  getStateSlice,
  getNameInitials,
  getColorInitial,
  convertGroupLink,
  convertWindowsLink,
  getUrlFromString,
  getGroupSlugChannel,
  getDomainFromUrl,
  stringCapitalisation,
  formatDuration,
  formatSecondsDuration,
  filterNumbers,
  blobToFile,
  getRedirectBaseUrl,
  getBaseDomain,
  getChannelLinksColor,
  getBlockButtonStyle,
  getTextButtonStyle,
  supportVideoFormats,
  isVideoFile,
  getAccessLabel,
  secondsToContentDuration,
  getOutlineButtonStyle,
  getBlockInverseButtonStyle,
  customThemeSet,
  getCoursePriceLabelStyle,
  getPlanInterval,
  formatGroupPlans,
  toTitleCase,
  getCourseButtonAction,
  getCourseButtonActionText,
  startCourse,
  sanitizeLibraryContentSlug,
  objectToQueryString,
  toLowerCase,
  sanitizeQuillValue,
  mBToGb,
  getDateDiffInDays,
  getDateDiffInHours,
  isTrialPast,
  getAvailableCurrencies,
  getCurrencySymbol,
  getResourceCurrency,
  getTimeFromDate,
  getFormattedDateFromDate,
  getDateFromString,
  getGroupPlan,
  planBlocksCourseActivation,
  getLibraryResourceFileLimit,
  planBlocksLibraryResourceFile,
  isGroupOnPlan,
  updateArrayObject,
  getDateInCurrentTimezone,
  getUserTimezone,
  getTimezoneAbbreviation,
  getUserTimezoneAbbreviation,
  getUserPlan,
  getTimezoneDateFromString,
  getDateInGivenTimezone,
  getCommunityTimezone,
  getDateObjectFromGivenTimezone,
  changeTimezone,
  getTimezoneUTCOffset,
  getUTCDateFromTimezoneDate,
  getImageExtension,
  isQuillEmpty,
  hexToRgb,
  formatLessonDescription,
  sanitizeLessonDescription,
  getMemberType,
  stripHTML,
  stripHTMLAndKeepLineBreaks,
  isLink,
  getReadableTimezoneOffset,
  getVideoTypeAndIdFromUrl,
  validateFileName,
  getOutButtonStyle: getOutlineButtonStyle,
  useNbeaFlow,
  OAuthActivated,
  userIsOnResourseSegment,
  getLockScreenDetails,
  userIsOnResourcePlan,
  customSignUpIsSetUp,
  isGroupOnFreePlan,
  isGroupOnDemoPlan,
  groupHasNotChosenPlan,
  isGroupDataReady,
  isGroupMember,
  convertDateStringToUsertimezoneFormat,
  getReorderedResourcesUsingDraggable,
  toSentenceCase,
  removeTags,
  decodeHtml,
  removeFormdataExtraNewLines,
  isNumeric,
  getGroupSubdomainFromSlug,
  getListFromEnum,
  sentenceCase,
  validateEmail,
  getPlanIntervally,
  getUserSubscriptionTrialEndDate,
  getPlanCycle,
  isSubscriptionPaymentFailedOrPastDue,
  isOneTimeSubscriptionPlan,
  userHasCardOnFile,
  userTrialEndDate,
  userIsOnTrial,
  getTrialDaysRemaining,
  getTrialDaysUsed,
  getUserSubscriptionStartDate,
  getProratedTrialDays,
  isSubscriptionPaymentCanceled,
  subscriptionHasActivePreviousPlan,
  isSameDayEvent,
  onboardingPopUpShouldOpen,
  newMemberCheck,
  slugify,
  validateSpecialChars,
  convertHeicToJpeg,
  getSubdomainIndex,
  getDateTimeString,
  addDaysToDate,
  isActiveCommunityMember,
  isChannelManaged,
  isChannelMember,
  isMembershipPendingApproval,
  MaxPhotoFileSize,
  validatePhotoFileSize,
  toastPhotoUploadFailed,
  getBunnyVideoLibraryId,
  getBunnyMP4VideoUrl,
  getBunnyEmbedUrl,
  parseBunnyCaptionTracks,
  encodeURLLastPathSegment,
  scrollToTop,
  isEmptyText,
  postDescCount,
  truncateString,
  replaceNbspWithSpace,
  replaceAmpersandWithCharacter,
  groupStorageAddonIsInactive,
  planBlocksPublicVisibility,
  planBlocksVisibility,
  publicVisibilityPrompt,
  planBlocksSegmentVisibility,
  exceedsPlanUploadLimit,
  getUploadLimitLabel,
  planBlocksFeature,
  planBlocksDirectVideoUpload,
  fileInputAcceptsVideo,
  groupHasMobileAddOn,
  groupStorageLimitIsMaxedOut,
  groupStorageUsageIsEightyPercent,
  groupStorageLimitWillMaxOut,
  isWithinPastXDays,
  hasIntegration,
  shouldDownloadShowSmartBanner,
  validateURL,
  validateURLReturnError,
  moderatorHasAdditionalPerms,
  moderatorIsAnAuthorizedForChannels,
  moderatorIsAnAuthorizedForCourses,
  moderatorIsAuthorizedForCourse,
  determineRolelabel,
  getExpirationDate,
  getNewUserUpgradeBefore,
  getNewUserOfferDaysLeft,
  isNewCommunityCoupon,
  newCommunityCouponClicked,
  markNewCommunityCouponClicked,
  clearNewCommunityCouponClicked,
  getTopOffsetForMobile,
  isCommunityOnMonthlyTrialExcludingBusinessPlan,
  isGroupTrialing,
  // applyCouponBtnClicked,
  getDefaultCommunityPage,
  getDefaultPageKey,
  ensureHttps,
  removeProtocolFromURL,
  canUserPostInChannel,
  customGroupThemeSet,
  getPageNumber,
  getDaysLeft,
  getYouTubeVideoId,
  getTrialInfo,
  getStripePromise,
  isSubscriptionPaymentCanceledAndHasTemp,
  buildQueryString,
  formatStorage,
  openGleap,
  openGleapConversation,
  userHasPermissionToLibraryContent,
  getPathFromUrl,
  canDowngradeExtraStorage,
  getNextUpgradePlan,
  shouldSuggestUpgrade,
  roundPriceToWhole,
  getNextPlanToUpgradeTo,
  planIsAuthorized,
  canDowngradePlan,
  getDowngradeBlockers,
  isOverPlanLimit,
  getAutoFontColor,
  getHeaderChallenge,
  isMembershipHasIncompleteSubscriptionData,
  getChannelHashTagColor,
  getSidebarActionLinkStyle,
  darkenBackground,
  createGradient,
  getAvailableTeamSize,
  truncateHTML,
  truncateDraftJs,
  parseDraftJson,
  normalizeDraftRaw,
  isEmptyDraftRaw,
  getFirstWordsFromHTML,
  shortTimeAgo,
};
